import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { morph } from '@theme/morph';
import { morphSection, sectionRenderer, normalizeSectionId, buildSectionSelector } from '@theme/section-renderer';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that manages the personalisation modal.
 *
 * @extends DialogComponent
 */
export class PersonaliseDialogComponent extends DialogComponent {
  requiredRefs = ['dialog', 'saveButton', 'closeButton'];
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.selectedFont = null;
    this.selectedColor = null;
    
    // Initialize personalisationData structure (fresh on each page load)
    this.personalisationData = {
      name: '',
      font: null,
      color: null,
      dob: null,
      schoolYear: null,
      name1: null,
      name2: null,
      name3: null,
      name4: null,
      textbox: null,
      message: null,
      optionalDob: null,
      time: null,
      weight: null,
      babyName: null,
      kidName: null,
      mumName: null
    };
    
    // Set up direct click handlers for close buttons to stop propagation
    setTimeout(() => {
      this.#setupCloseButtonHandlers();
    }, 100);
    
    // Set up form submit listener
    setTimeout(() => {
      this.setupFormSubmitListenerForCurrentSession();
    }, 100);
    
    // Set form attribute on Baby/Kid/Mum inputs
    this.#setFormAttributeOnInputs();
    
    // Set up event listeners for input changes to update save button
    this.#setupInputChangeListeners();

    // Set up variant change listener for dynamic personalization (cb metafields)
    this.#setupCbVariantChangeListener();

    // Set up variant color change listener (when user selects color in modal)
    this.#setupVariantColorChangeListener();

    // If coming from collection page: auto-add ?customize=true to URL (product has personalisation + full-screen metafield)
    this.#maybeAddCustomizeParamFromCollection();
    // Auto-open personalisation when product page loads with ?customize=true (product has personalisation + full-screen metafield)
    this.#maybeAutoOpenPersonalisation();
  }

  /**
   * When navigating from a collection page to product page: adds ?customize=true to URL
   * so the personalisation modal can auto-open (product has personalisation + full-screen metafield)
   * @private
   */
  #maybeAddCustomizeParamFromCollection() {
    if (this.dataset.autoOpenPersonalisation !== 'true') return;
    if (this.dataset.context === 'cart-drawer') return;
    if (this.hasAttribute('data-quick-add')) return;

    const referrer = document.referrer || '';
    if (!referrer.includes('/collections/')) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('customize') === 'true') return;

    url.searchParams.set('customize', 'true');
    history.replaceState({}, '', url.toString());
  }

  /**
   * Auto-opens the personalisation modal when product page is loaded with ?customize=true
   * Only when: product has personalisation options, metafield cb_full_screen_personalisation is true, and not quick-add/cart context
   * @private
   */
  #maybeAutoOpenPersonalisation() {
    if (this.dataset.autoOpenPersonalisation !== 'true') return;
    if (this.dataset.context === 'cart-drawer') return;
    if (this.hasAttribute('data-quick-add')) return;

    const url = new URL(window.location.href);
    if (url.searchParams.get('customize') !== 'true') return;

    // Prevent multiple dialogs from auto-opening (product page may have multiple personalise-dialog elements)
    if (window._personalisationAutoOpened) return;
    window._personalisationAutoOpened = true;

    // Small delay to ensure DOM is ready and refs are populated
    requestAnimationFrame(() => {
      setTimeout(() => {
        if (this.refs?.dialog?.open) return;
        if (typeof this.showDialog === 'function') {
          this.showDialog();
        }
      }, 100);
    });
  }

  /**
   * Parses the cb personalization variants map from the embedded script
   * @returns {{ product: { image?: string, position?: object, fallbackImage?: string }, variants: Record<string, { image: string, position: object }>, variantFallbackImages: Record<string, string> } | null}
   */
  #getCbPersonalizationMap() {
    const script = this.querySelector('script[data-cb-personalization-variants]');
    if (!script?.textContent) return null;
    try {
      return JSON.parse(script.textContent);
    } catch {
      return null;
    }
  }

  /**
   * Updates the dynamic personalization preview when the selected variant changes.
   * Image hierarchy: variant metafield → product metafield → variant featured → product featured.
   * Position is optional (used for overlay when available).
   * @param {string} variantId - The selected variant ID
   */
  #updateCbPreviewForVariant(variantId) {
    const map = this.#getCbPersonalizationMap();
    if (!map) return;

    // Normalise to string so lookup in JSON-parsed map works (keys are strings)
    const variantKey = variantId != null ? String(variantId) : '';

    const { product, variants, variantFallbackImages } = map;
    let showDynamic = false;
    let imageUrl = '';
    let positionJson = '';

    // Use variant image when available (position optional)
    if (variantKey && variants && variants[variantKey]?.image) {
      showDynamic = true;
      imageUrl = variants[variantKey].image;
      const pos = variants[variantKey].position;
      if (pos != null && pos !== 'null') {
        positionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
      }
    } else if (product?.image) {
      showDynamic = true;
      imageUrl = product.image;
      const pos = product.position;
      if (pos != null && pos !== 'null') {
        positionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
      }
    }

    const dynamicWrap = this.querySelector('[data-cb-dynamic-wrap]');
    const fallbackWrap = this.querySelector('[data-cb-fallback-wrap]');
    const placeholder = this.querySelector('[data-cb-placeholder]');

    if (showDynamic) {
      this.dataset.cbPersonalizationImage = imageUrl;
      this.dataset.cbPersonalizationPosition = positionJson;
      const dynamicImg = dynamicWrap?.querySelector('.personalise-modal__preview-image--dynamic');
      if (dynamicImg) {
        dynamicImg.src = imageUrl;
      }
      if (dynamicWrap) dynamicWrap.style.display = '';
      if (fallbackWrap) fallbackWrap.style.display = 'none';
      if (placeholder) placeholder.style.display = 'none';
      this.updateCbPreviewOverlay();
    } else {
      delete this.dataset.cbPersonalizationImage;
      delete this.dataset.cbPersonalizationPosition;
      if (dynamicWrap) dynamicWrap.style.display = 'none';
      if (fallbackWrap) {
        fallbackWrap.style.display = '';
        const fallbackImg = fallbackWrap.querySelector('.personalise-modal__preview-image');
        const fallbackImageUrl = variantFallbackImages?.[variantKey] || product?.fallbackImage;
        if (fallbackImg && fallbackImageUrl) {
          fallbackImg.src = fallbackImageUrl;
          // Clear srcset so the browser uses src (Shopify image_tag adds srcset)
          fallbackImg.removeAttribute('srcset');
        }
      }
      if (placeholder && !fallbackWrap) placeholder.style.display = '';
    }

    // DOB position: variant first, then product
    let dobPositionJson = '';
    if (variantKey && variants && variants[variantKey]?.dobPosition != null && variants[variantKey].dobPosition !== 'null') {
      const pos = variants[variantKey].dobPosition;
      dobPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    } else if (product?.dobPosition != null && product.dobPosition !== 'null') {
      const pos = product.dobPosition;
      dobPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    }
    if (dobPositionJson) {
      this.dataset.cbDobPersonalizationPosition = dobPositionJson;
    } else {
      delete this.dataset.cbDobPersonalizationPosition;
    }

    let tobPositionJson = '';
    if (variantKey && variants && variants[variantKey]?.tobPosition != null && variants[variantKey].tobPosition !== 'null') {
      const pos = variants[variantKey].tobPosition;
      tobPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    } else if (product?.tobPosition != null && product.tobPosition !== 'null') {
      const pos = product.tobPosition;
      tobPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    }
    if (tobPositionJson) {
      this.dataset.cbTobPersonalizationPosition = tobPositionJson;
    } else {
      delete this.dataset.cbTobPersonalizationPosition;
    }

    let weightPositionJson = '';
    if (variantKey && variants && variants[variantKey]?.weightPosition != null && variants[variantKey].weightPosition !== 'null') {
      const pos = variants[variantKey].weightPosition;
      weightPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    } else if (product?.weightPosition != null && product.weightPosition !== 'null') {
      const pos = product.weightPosition;
      weightPositionJson = typeof pos === 'string' ? pos : JSON.stringify(pos);
    }
    if (weightPositionJson) {
      this.dataset.cbWeightPersonalizationPosition = weightPositionJson;
    } else {
      delete this.dataset.cbWeightPersonalizationPosition;
    }

    this.#updateCbOptionalFieldPreviewOverlays();

    // Update color grid for new variant (variant-aware text colours)
    this.#updateColorGridForVariant(variantKey, map);

    // Clear saved color from form/window if it's no longer valid for the new variant
    this.#clearInvalidColorOnVariantChange(variantKey, map);
  }

  /**
   * Clears the saved personalisation color from form and window.currentPersonalisation when the
   * variant changes and the new variant doesn't support that color. Updates the button text.
   * @param {string} variantKey - The selected variant ID (string)
   * @param {{ variantColors?: Record<string, Array<{value: string, key: string}>>, productColors?: Array<{value: string, key: string}> }} map - The cb personalization map
   * @private
   */
  #clearInvalidColorOnVariantChange(variantKey, map) {
    // Cart drawer dialog: do NOT touch product page forms – we're editing a cart item, not the product page
    // Only check this dialog's context (not window.cartPersonalizationContext) so product-page dialog
    // still shows "Please select colour" when user changes variant on product page while cart modal is open
    if (this.dataset.context === 'cart-drawer') {
      return;
    }

    const { variantColors = {}, productColors = [] } = map;
    const allowedColors = (variantKey && variantColors?.[variantKey]) ?? productColors ?? [];
    const allowedValues = new Set(allowedColors.flatMap((c) => [c.value?.toLowerCase(), c.key?.toLowerCase()]).filter(Boolean));

    const productId = this.dataset?.productId;
    const forms = this.#getAllProductForms();

    let savedColor = null;
    for (const form of forms) {
      const colorInput = form.querySelector('input[name="properties[Text Colour]"]');
      if (colorInput?.value) {
        savedColor = colorInput.value;
        break;
      }
    }
    if (!savedColor && productId && window.currentPersonalisation?.[productId]?.color) {
      savedColor = window.currentPersonalisation[productId].color;
    }
    if (!savedColor) return;

    const isColorValid = allowedValues.has(savedColor.toLowerCase?.() ?? savedColor);
    if (isColorValid) return;

    // Clear color from form inputs
    for (const form of forms) {
      const colorInput = form.querySelector('input[name="properties[Text Colour]"]');
      if (colorInput) colorInput.remove();
    }
    // Clear from window.currentPersonalisation
    if (productId && window.currentPersonalisation?.[productId]) {
      delete window.currentPersonalisation[productId].color;
      if (window.currentPersonalisation._latest?.color) {
        delete window.currentPersonalisation._latest.color;
      }
    }

    if (typeof window.updatePersonaliseButtonText === 'function') {
      window.updatePersonaliseButtonText(forms[0] || null);
    }

    // When new variant requires color: show "Please select colour", uncheck and hide confirmation
    if (allowedColors.length > 0) {
      this.#showPersonaliseColorRequiredState(productId, forms);
    }
  }

  /**
   * Shows "Please select colour" message, unchecks and hides confirmation when color was cleared due to variant change.
   * @param {string} productId - The product ID
   * @param {HTMLFormElement[]} forms - The product forms
   * @private
   */
  #showPersonaliseColorRequiredState(productId, forms) {
    const searchRoots = forms.map((f) => f.closest('product-form-component') || f.closest('#quick-add-modal-content')).filter(Boolean);
    const stickyBar = productId ? document.querySelector(`sticky-add-to-cart[data-product-id="${productId}"]`) : null;
    const uniqueRoots = [...new Set([...searchRoots, ...(stickyBar ? [stickyBar] : [])])];

    for (const form of forms) {
      form.setAttribute('data-personalisation-color-required', 'true');
    }
    for (const searchRoot of uniqueRoots) {
      const confirmationContainer = searchRoot.querySelector('[data-personalise-confirmation]');
      const checkbox = confirmationContainer?.querySelector('[data-personalise-confirm-checkbox]');
      if (checkbox) checkbox.checked = false;
      if (confirmationContainer) confirmationContainer.style.display = 'none';

      // Skip "Please select colour" message in sticky bar (user preference: don't show in sticky bar)
      const isStickyBar = searchRoot.tagName === 'STICKY-ADD-TO-CART';
      if (!isStickyBar) {
        let msgEl = searchRoot.querySelector('[data-personalise-color-required-msg]');
        if (!msgEl) {
          msgEl = document.createElement('p');
          msgEl.className = 'personalise-color-required-msg';
          msgEl.setAttribute('data-personalise-color-required-msg', '');
          msgEl.textContent = 'Please again select text colour for this variant';
          msgEl.style.cssText = 'margin: 0.25rem 0 0 0; color: #c00; font-size: 0.875rem;';
          const personaliseBtn = searchRoot.querySelector('[data-personalise-button]');
          if (personaliseBtn && personaliseBtn.nextElementSibling) {
            personaliseBtn.nextElementSibling.before(msgEl);
          } else if (personaliseBtn) {
            personaliseBtn.after(msgEl);
          } else {
            searchRoot.querySelector('.product-form-buttons')?.prepend(msgEl);
          }
        }
        if (msgEl) msgEl.style.display = '';
      } else {
        // Ensure any existing message in sticky bar stays hidden
        const msgEl = searchRoot.querySelector('[data-personalise-color-required-msg]');
        if (msgEl) msgEl.style.display = 'none';
      }
    }

    if (typeof window.updateAddToCartButtonState === 'function') {
      window.updateAddToCartButtonState();
    }

    const quickAddModal = document.getElementById('quick-add-modal-content');
    if (quickAddModal?.hasAttribute('data-build-your-set') && typeof window.updateBuildYourSetAddToSetState === 'function') {
      window.updateBuildYourSetAddToSetState(quickAddModal);
    }

    // Directly disable add to cart (updateAddToCartButtonState may return early when confirmation is hidden)
    for (const root of uniqueRoots) {
      const addToCartContainers = root.querySelectorAll('add-to-cart-component');
      addToCartContainers.forEach((c) => {
        if (c.refs?.addToCartButton) c.disable();
      });
      const sticky = root.tagName === 'STICKY-ADD-TO-CART' ? root : root.querySelector('sticky-add-to-cart');
      const stickyBtn = sticky?.refs?.addToCartButton || sticky?.querySelector('.sticky-add-to-cart__button');
      if (stickyBtn && sticky?.dataset?.variantAvailable === 'true') {
        stickyBtn.disabled = true;
      }
    }
  }

  /**
   * Rebuilds the color grid when variant changes, using variant/product colors from the map.
   * @param {string} variantKey - The selected variant ID (string)
   * @param {{ variantColors?: Record<string, Array<{value: string, key: string}>>, productColors?: Array<{value: string, key: string}> }} map - The cb personalization map
   * @private
   */
  #updateColorGridForVariant(variantKey, map) {
    const colorGrid = this.refs?.colorGrid || this.querySelector('.personalise-modal__color-grid');
    if (!colorGrid) return;

    const { variantColors = {}, productColors = [] } = map;
    const colors = (variantKey && variantColors?.[variantKey]) ?? productColors ?? [];

    // Capture saved color before clearing (for re-apply after grid rebuild when opening for edit)
    let savedColor = this.personalisationData?.color || this.selectedColor;
    if (!savedColor) {
      for (const form of this.#getAllProductForms()) {
        const colorInput = form.querySelector('input[name="properties[Text Colour]"]:checked, input[name="properties[Text Colour]"]');
        if (colorInput?.value) {
          savedColor = colorInput.value;
          break;
        }
      }
    }
    if (!savedColor && this.dataset?.productId && window.currentPersonalisation?.[this.dataset.productId]?.color) {
      savedColor = window.currentPersonalisation[this.dataset.productId].color;
    }

    // Set up click delegation once (for dynamically added buttons)
    if (!colorGrid.hasAttribute('data-color-click-delegation')) {
      colorGrid.setAttribute('data-color-click-delegation', 'true');
      colorGrid.addEventListener('click', (e) => {
        const btn = e.target.closest('.personalise-modal__color-button');
        if (btn) this.selectColor(e);
      });
    }

    const colorField = colorGrid.closest('.personalise-modal__field');
    if (!colorField) return;

    // When variant has no color options, hide the color field and clear selection
    if (colors.length === 0) {
      colorField.style.display = 'none';
      colorGrid.innerHTML = '';
      this.selectedColor = null;
      this.personalisationData.color = null;
      this.updateSaveButton();
      this.updateCbPreviewOverlay();
      return;
    }

    colorField.style.display = '';

    // Build new color buttons (match Liquid structure: label > input + thumbnail with swatch)
    const fragment = document.createDocumentFragment();
    colors.forEach(({ value, key }) => {
      const label = document.createElement('label');
      label.className = 'personalise-modal__color-button variant-option__button-label variant-option__button-label--image-thumbnail';
      label.dataset.color = key;
      label.title = value;
      label.setAttribute('ref', 'colorButton');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'personalise-color';
      input.value = value;
      label.appendChild(input);

      const thumbWrap = document.createElement('div');
      thumbWrap.className = 'variant-option__image-thumbnail';

      const iconWrap = document.createElement('div');
      iconWrap.className = 'personalise-modal__color-icon';

      const swatch = document.createElement('span');
      swatch.className = 'swatch swatch--unscaled';
      swatch.style.cssText = `--swatch-background: ${key}; background-color: ${key};`;
      iconWrap.appendChild(swatch);
      thumbWrap.appendChild(iconWrap);
      label.appendChild(thumbWrap);

      fragment.appendChild(label);
    });

    colorGrid.innerHTML = '';
    colorGrid.appendChild(fragment);

    this.selectedColor = null;
    this.personalisationData.color = null;
    this.updateSaveButton();
    this.updateCbPreviewOverlay();

    // Re-attach input listeners for save button validation
    this.#attachInputListeners();

    // Re-apply saved color if valid for this variant (fixes modal open pre-fill when variant map rebuilds grid)
    if (savedColor) {
      const allowedValues = new Set(colors.flatMap((c) => [c.value?.toLowerCase(), c.key?.toLowerCase()]).filter(Boolean));
      if (allowedValues.has(String(savedColor).toLowerCase())) {
        this.selectColorByName(savedColor);
        return;
      }
    }
    // When variant changed in modal (or no saved color): select first text colour by default
    // Only auto-select when the dialog is OPEN – avoids capturing unintended values when user
    // changes variant in quick-add without ever opening personalise (e.g. Add to Set bug)
    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (dialog?.open) {
      requestAnimationFrame(() => {
        const firstRadio = colorGrid.querySelector('input[type="radio"]');
        if (firstRadio) {
          firstRadio.checked = true;
          const label = firstRadio.closest('.personalise-modal__color-button');
          if (label) {
            colorGrid.querySelectorAll('.personalise-modal__color-button').forEach((l) => l.classList.remove('personalise-modal__color-button--selected'));
            label.classList.add('personalise-modal__color-button--selected');
          }
          this.selectedColor = firstRadio.value;
          this.personalisationData.color = firstRadio.value;
          this.updateSaveButton();
          this.updateCbPreviewOverlay();
        }
      });
    }
  }

  /**
   * Sets up listener for variant changes to update dynamic personalization preview.
   * Only active for product-page context (not cart-drawer, quick-add).
   * @private
   */
  #setupCbVariantChangeListener() {
    if (this.dataset.context !== 'product-page') return;
    if (!this.querySelector('script[data-cb-personalization-variants]')) return;

    const handleVariantUpdate = (event) => {
      const productId = event.detail?.data?.productId;
      const ourProductId = this.dataset.productId;
      if (ourProductId && productId && productId !== ourProductId) return;

      const variant = event.detail?.resource;
      const variantId = variant?.id?.toString?.() ?? variant?.id;
      if (!variantId) return;

      // Store so when modal opens we sync to this variant (form may not have updated yet)
      this._lastVariantIdFromProductPage = String(variantId);
      this.#updateCbPreviewForVariant(variantId);
    };

    // Listen on document so we catch variant updates from any section (variant picker may be in different block)
    const target = document.body;
    target.addEventListener(ThemeEvents.variantUpdate, handleVariantUpdate);

    this._cbVariantUpdateHandler = handleVariantUpdate;
    this._cbVariantUpdateTarget = target;
  }

  /**
   * Finds the add-to-cart form (product page or quick-add modal).
   * Personalise-dialog is often a sibling of product-form-component (not inside it), so we find by product ID.
   * @returns {HTMLFormElement|null}
   */
  #getProductForm() {
    const forms = this.#getAllProductForms();
    return forms[0] || null;
  }

  /**
   * Finds ALL add-to-cart forms for this product (for variant sync).
   * Needed because: (1) product page has main form, (2) sticky bar uses same form, (3) quick-add has its own form in modal.
   * @returns {HTMLFormElement[]}
   */
  #getAllProductForms() {
    const productId = this.dataset?.productId;
    const forms = [];

    const addFormFromComponent = (comp) => {
      const form = comp?.querySelector('form[data-type="add-to-cart-form"]');
      if (form && !forms.includes(form)) forms.push(form);
    };

    // 1. Quick-add: when we're INSIDE quick-add modal, get form from modal
    const quickAddModal = this.closest('#quick-add-modal-content');
    if (quickAddModal) {
      const formComponent = productId
        ? quickAddModal.querySelector(`product-form-component[data-product-id="${productId}"]`)
        : quickAddModal.querySelector('product-form-component');
      addFormFromComponent(formComponent);
      if (forms.length > 0) return forms;
    }

    // 2. Product page: find form(s) in main section (sticky bar uses same form, but form lives in section)
    if (productId) {
      const section = this.closest('.shopify-section');
      const searchRoot = section || document;
      const formComponent = searchRoot.querySelector(`product-form-component[data-product-id="${productId}"]`);
      addFormFromComponent(formComponent);
    }

    // 3. Also search document for any other matching form (e.g. when dialog is in drawer/portal)
    if (productId) {
      document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`).forEach(addFormFromComponent);
    }

    // 4. Fallback: closest product-form-component
    const formComponent = this.closest('product-form-component');
    addFormFromComponent(formComponent);

    // 5. Last resort: any form for this product
    if (forms.length === 0) {
      const anyForm = document.querySelector('form[data-type="add-to-cart-form"]');
      if (anyForm) forms.push(anyForm);
    }
    return forms;
  }

  /**
   * Sets up listener for variant color selection inside the modal.
   * Updates form variant ID and preview when user selects a color variant.
   * @private
   */
  #setupVariantColorChangeListener() {
    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (!dialog) {
      setTimeout(() => this.#setupVariantColorChangeListener(), 100);
      return;
    }

    const attachVariantColorListeners = () => {
      const variantColorRadios = this.querySelectorAll('.personalise-modal__variant-color-grid input[type="radio"][data-variant-id]');
      variantColorRadios.forEach((radio) => {
        if (radio.dataset.variantColorListener) return;
        radio.dataset.variantColorListener = 'true';
        radio.addEventListener('change', this.#handleVariantColorChange.bind(this));
      });
    };

    attachVariantColorListeners();

    const observer = new MutationObserver(() => attachVariantColorListeners());
    observer.observe(dialog, { childList: true, subtree: true });
  }

  /**
   * Handles variant color selection change inside the modal.
   * Updates form variant ID, preview image, and dispatches variant update event.
   * @private
   */
  /**
   * Parses all product variants from the embedded JSON script tag.
   * Each entry has id, option1, option2, option3.
   * @returns {Array<{id: number, option1: string, option2: string|null, option3: string|null}>}
   */
  #getProductVariantsMap() {
    const script = this.querySelector('script[data-product-variants-map]');
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch {
      return [];
    }
  }

  /**
   * Resolves the correct variant ID when a color swatch is selected in the modal.
   * Works for any number of option dimensions (size + color, size + color + material, etc.).
   *
   * Uses the embedded variants JSON to look up the variant that matches:
   *   - the newly selected color value (from the radio)
   *   - the same non-color option values as the currently selected variant on the page
   *
   * Falls back to data-variant-id when no better match is found.
   * @param {HTMLInputElement} radio
   * @returns {string|undefined}
   */
  #resolveVariantIdForColorChange(radio) {
    const directVariantId = radio.dataset?.variantId;
    const colorVariantIds = (radio.dataset?.variantIds || '').split(',').map((id) => id.trim()).filter(Boolean);

    if (colorVariantIds.length <= 1) return directVariantId || colorVariantIds[0];

    // Get currently selected variant (the one with the current size/capacity selection)
    const currentVariantId = this.#getVariantIdForSync();
    if (!currentVariantId) return directVariantId;

    const allVariants = this.#getProductVariantsMap();
    if (!allVariants.length) return directVariantId;

    const currentVariant = allVariants.find((v) => String(v.id) === String(currentVariantId));
    if (!currentVariant) return directVariantId;

    // The selected color value from the radio (exact case as Shopify stores it)
    const selectedColorValue = radio.value;
    const colorVariantIdSet = new Set(colorVariantIds);
    const optionKeys = ['option1', 'option2', 'option3'].filter((k) => currentVariant[k] != null);

    // Find the candidate variant where:
    //   - it is in the color's variant set
    //   - each option is either the new color value (the color dimension) OR matches the current variant (other dimensions)
    const match = allVariants.find((v) => {
      if (!colorVariantIdSet.has(String(v.id))) return false;
      return optionKeys.every((key) => v[key] === currentVariant[key] || v[key] === selectedColorValue);
    });

    return match ? String(match.id) : directVariantId;
  }

  #handleVariantColorChange(event) {
    const radio = event.target;
    if (!radio?.checked) return;

    const variantId = this.#resolveVariantIdForColorChange(radio);
    if (!variantId) return;

    this.#updateVariantColorSelectedValue(radio);

    // Update variant in ALL forms for this product (main page, sticky bar context, quick-add)
    const forms = this.#getAllProductForms();
    forms.forEach((form) => {
      const variantInput = form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.disabled = false;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });

    // Sync variant picker UI (color swatches on product page / quick-add)
    this.#syncVariantPickersToVariant(variantId);

    this.#updateCbPreviewForVariant(variantId);

    const productId = this.dataset?.productId;

    // On product page: update URL and dispatch VariantUpdateEvent with fetched HTML so price, media, etc. update
    const productPagePicker = productId
      ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
      : document.querySelector('variant-picker');
    const isProductPage = productPagePicker && !productPagePicker.closest('#quick-add-modal-content') && !window.cartPersonalizationContext;
    if (isProductPage) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      if (url.href !== window.location.href) {
        history.replaceState({}, '', url.toString());
      }
      const fullUrl = url.toString();
      fetch(fullUrl)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const textContent = doc.querySelector(`variant-picker[data-product-id="${productId}"] script[type="application/json"]`)?.textContent
            || doc.querySelector('variant-picker script[type="application/json"]')?.textContent;
          const variantResource = textContent ? (() => {
            try {
              return JSON.parse(textContent);
            } catch {
              return { id: Number(variantId) };
            }
          })() : { id: Number(variantId) };
          // Get fresh reference (DOM may have morphed after save)
          const picker = productId
            ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
            : document.querySelector('variant-picker');
          if (picker && !picker.closest('#quick-add-modal-content')) {
            // Morph variant picker from fetched HTML so selected style (swatch/thumbnail) updates correctly
            const newPicker = doc.querySelector(`variant-picker[data-product-id="${productId}"]`) || doc.querySelector('variant-picker');
            if (newPicker) {
              morph(picker, newPicker);
            }
            picker.dispatchEvent(new VariantUpdateEvent(variantResource, this.id || 'personalise-dialog', {
              productId: productId || '',
              html: doc
            }));
          }
        })
        .catch(() => {
          const picker = productId
            ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
            : document.querySelector('variant-picker');
          if (picker && !picker.closest('#quick-add-modal-content')) {
            picker.dispatchEvent(new VariantUpdateEvent({ id: Number(variantId) }, this.id || 'personalise-dialog', {
              productId: productId || '',
              html: null
            }));
          }
        });
    }

    // On quick-add: fetch product page with new variant and directly update the image
    // (Quick-add's VariantUpdateEvent handler needs html; we fetch and update manually here)
    const isQuickAddContext = this.hasAttribute('data-quick-add');
    const quickAddModal = isQuickAddContext ? document.getElementById('quick-add-modal-content') : null;
    if (quickAddModal) {
      const quickAddPicker = productId
        ? quickAddModal.querySelector(`variant-picker[data-product-id="${productId}"]`)
        : quickAddModal.querySelector('variant-picker');
      const productFormComponent = quickAddModal.querySelector('product-form-component');
      const productUrl = quickAddPicker?.dataset?.productUrl || productFormComponent?.dataset?.productUrl;
      if (productUrl) {
        const fetchUrl = productUrl.includes('?')
          ? `${productUrl.split('?')[0]}?variant=${variantId}`
          : `${productUrl}?variant=${variantId}`;
        const fullUrl = fetchUrl.startsWith('/')
          ? new URL(fetchUrl, window.location.origin).toString()
          : fetchUrl;
        fetch(fullUrl)
          .then((r) => r.text())
          .then((html) => {
            const doc = new DOMParser().parseFromString(html, 'text/html');
            const newMediaGallery = doc.querySelector('media-gallery');
            const newFirstImage =
              newMediaGallery?.querySelector(
                '.product-media img, slideshow-slide img, .product-media-container img, img[src], img[data-src]'
              ) || doc.querySelector('.product-information__media img, media-gallery img');
            const findCurrentImage = () =>
              quickAddModal.querySelector(
                '.product-information__media slideshow-slide:first-child img, .product-information__media .product-media img, .product-information__media .product-media-container img, .product-information__media img, media-gallery img'
              );
            const currentFirstImage = findCurrentImage();
            if (newFirstImage && currentFirstImage) {
              const newImageSrc =
                newFirstImage.getAttribute('src') ||
                newFirstImage.src ||
                newFirstImage.getAttribute('data-src') ||
                newFirstImage.getAttribute('srcset')?.trim().split(/\s+/)[0];
              if (newImageSrc && currentFirstImage.src !== newImageSrc) {
                currentFirstImage.style.transition = 'none';
                currentFirstImage.style.opacity = '1';
                currentFirstImage.src = newImageSrc;
                if (newFirstImage.getAttribute('srcset')) {
                  currentFirstImage.srcset = newFirstImage.getAttribute('srcset');
                }
                if (newFirstImage.getAttribute('sizes')) {
                  currentFirstImage.sizes = newFirstImage.getAttribute('sizes');
                }
                if (newFirstImage.getAttribute('alt')) {
                  currentFirstImage.alt = newFirstImage.getAttribute('alt');
                }
              }
            }
          })
          .catch(() => {});
      }
    }

    const variantResource = { id: Number(variantId) };
    const section = this.closest('.shopify-section') || document.body;
    section.dispatchEvent(new VariantUpdateEvent(variantResource, this.id || 'personalise-dialog', {
      productId: productId || '',
      html: null
    }));

    this.updateSaveButton();
  }

  /**
   * Syncs the variant color radio selection to match the current variant from the form.
   * Called when modal opens so the correct variant is shown as selected.
   * Handles products with color-only and color+size: checks data-variant-id and data-variant-ids.
   * @param {string} variantId - The current variant ID from the form
   * @private
   */
  #syncVariantColorSelection(variantId) {
    if (!variantId) return;
    const variantIdStr = String(variantId);
    const radios = this.querySelectorAll('.personalise-modal__variant-color-grid input[type="radio"][data-variant-id]');
    let checkedRadio = null;
    radios.forEach((radio) => {
      const matchesId = radio.dataset.variantId === variantIdStr;
      const variantIds = radio.dataset.variantIds || '';
      const matchesIds = variantIds.split(',').map((id) => id.trim()).includes(variantIdStr);
      radio.checked = matchesId || matchesIds;
      if (radio.checked) checkedRadio = radio;
    });
    this.#updateVariantColorSelectedValue(checkedRadio);
  }

  /**
   * Syncs variant picker UI (product page / quick-add) to show the selected variant.
   * Called when color is changed in the personalisation modal.
   * @param {string} variantId - The selected variant ID
   * @private
   */
  #syncVariantPickersToVariant(variantId) {
    if (!variantId) return;
    const variantIdStr = String(variantId);
    const productId = this.dataset?.productId;
    const variantPickers = productId
      ? document.querySelectorAll(`variant-picker[data-product-id="${productId}"]`)
      : document.querySelectorAll('variant-picker');
    variantPickers.forEach((picker) => {
      const inputs = picker.querySelectorAll('input[type="radio"][data-variant-id], input[type="radio"][data-variant-ids]');
      let matchingInput = null;
      for (const input of inputs) {
        const matchesId = input.dataset.variantId === variantIdStr;
        const variantIds = (input.dataset.variantIds || '').split(',').map((id) => id.trim());
        const matchesIds = variantIds.includes(variantIdStr);
        if (matchesId || matchesIds) {
          matchingInput = input;
          break;
        }
      }
      if (matchingInput) {
        picker.updateSelectedOption?.(matchingInput);
        const fieldset = matchingInput.closest('fieldset');
        const valueSpan = fieldset?.querySelector('.variant-option__selected-value');
        if (valueSpan) {
          valueSpan.textContent = matchingInput.value || '';
        }
      }
    });
  }

  /**
   * Updates the selected value label text above the variant color swatches.
   * @param {HTMLInputElement|null} checkedRadio - The checked radio input
   * @private
   */
  #updateVariantColorSelectedValue(checkedRadio) {
    const span = this.querySelector('[data-variant-color-selected-value]');
    if (!span) return;
    span.textContent = checkedRadio?.value ?? '';
  }

  /**
   * Gets the current variant ID for sync - from product form (product page/quick-add) or cart context (cart drawer).
   * @returns {string|null} - The variant ID to sync
   * @private
   */
  #getVariantIdForSync() {
    // Cart drawer context: use variant from cart line
    const cartContext = window.cartPersonalizationContext;
    if (cartContext?.variantId) {
      return String(cartContext.variantId);
    }
    // Quick-add: use form in modal only
    if (this.closest('#quick-add-modal-content')) {
      const productForm = this.#getProductForm();
      const variantInput = productForm?.querySelector('input[name="id"]');
      if (variantInput?.value) return String(variantInput.value);
      if (this._openingVariantId) {
        const id = this._openingVariantId;
        this._openingVariantId = null;
        return id;
      }
      return null;
    }
    // Product page: prefer last variant from VariantUpdateEvent (fires when user selects; form can lag after morph)
    if (this._lastVariantIdFromProductPage) {
      return this._lastVariantIdFromProductPage;
    }
    const productForm = this.#getProductForm();
    if (productForm) {
      const variantInput = productForm.querySelector('input[name="id"]');
      if (variantInput?.value) {
        return String(variantInput.value);
      }
    }
    if (this._openingVariantId) {
      const id = this._openingVariantId;
      this._openingVariantId = null;
      return id;
    }
    return null;
  }

  /**
   * Syncs variant color selection and preview image when modal opens.
   * Handles both product form context (product page, quick-add) and cart drawer context.
   * @private
   */
  #syncVariantAndPreviewOnOpen() {
    const variantId = this.#getVariantIdForSync();
    if (variantId) {
      this.#updateCbPreviewForVariant(variantId);
      this.#syncVariantColorSelection(variantId);
    }
  }

  /**
   * Sets up event listeners for all input changes to update save button state
   * @private
   */
  #setupInputChangeListeners() {
    // Use a MutationObserver to watch for dynamically added fields
    const observer = new MutationObserver(() => {
      this.#attachInputListeners();
    });
    
    // Start observing when dialog is available
    const checkDialog = () => {
      const dialog = this.refs?.dialog || this.querySelector('dialog');
      if (dialog) {
        observer.observe(dialog, { childList: true, subtree: true });
        // Initial attachment
        this.#attachInputListeners();
      } else {
        setTimeout(checkDialog, 100);
      }
    };
    
    setTimeout(checkDialog, 100);
  }
  
  /**
   * Attaches event listeners to all inputs for save button validation
   * @private
   */
  #attachInputListeners() {
    const dialog = this.refs?.dialog || this.querySelector('dialog');
    if (!dialog) return;
    
    // Listen for color radio button changes
    const colorRadios = dialog.querySelectorAll('.personalise-modal__color-grid input[type="radio"]');
    colorRadios.forEach(radio => {
      // Remove existing listener to avoid duplicates
      radio.removeEventListener('change', this.#handleColorRadioChange);
      radio.addEventListener('change', this.#handleColorRadioChange);
    });
    
    // Listen for all other input/textarea/select changes
    const allInputs = dialog.querySelectorAll('input:not([type="radio"]):not([type="checkbox"]), textarea, select');
    allInputs.forEach(input => {
      // Skip if already has listener (check by data attribute)
      if (input.dataset.hasSaveButtonListener) return;
      
      input.dataset.hasSaveButtonListener = 'true';
      input.addEventListener('input', () => {
        this.updateSaveButton();
        if (
          input.name === 'properties[Date of Birth]' ||
          input.id === 'dob_field_val' ||
          input.name === 'properties[Time]' ||
          input.name === 'properties[Weight]'
        ) {
          this.#updateCbOptionalFieldPreviewOverlays();
        } else {
          this.updateCbPreviewOverlay();
        }
      });
      input.addEventListener('change', () => {
        this.updateSaveButton();
        if (
          input.name === 'properties[Date of Birth]' ||
          input.id === 'dob_field_val' ||
          input.name === 'properties[Time]' ||
          input.name === 'properties[Weight]'
        ) {
          this.#updateCbOptionalFieldPreviewOverlays();
        } else {
          this.updateCbPreviewOverlay();
        }
      });
    });
    
    // Listen for checkbox changes
    const checkboxes = dialog.querySelectorAll('input[type="checkbox"]');
    checkboxes.forEach(checkbox => {
      if (checkbox.dataset.hasSaveButtonListener) return;
      checkbox.dataset.hasSaveButtonListener = 'true';
      checkbox.addEventListener('change', () => this.updateSaveButton());
    });
  }
  
  /**
   * Handles color radio button change events
   * @private
   */
  #handleColorRadioChange = (event) => {
    const radio = event.target;
    if (radio.checked) {
      const colorButton = radio.closest('.personalise-modal__color-button');
      if (colorButton) {
        const colorName = colorButton.dataset.color;
        this.selectedColor = colorName;
        this.personalisationData.color = colorName;
      }
    }
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  }

  /**
   * Sets up direct click handlers for close and cancel buttons to prevent event bubbling
   * @private
   */
  #setupCloseButtonHandlers() {
    
    // Store reference to component for use in event handlers
    const component = this;
    
    const dialogElement = this.refs?.dialog || this.querySelector('dialog');
    
    if (!dialogElement) {
      // Dialog element not ready yet, try again later
      setTimeout(() => this.#setupCloseButtonHandlers(), 50);
      return;
    }
    
    // DIRECT HANDLER FUNCTION - MUST stop propagation IMMEDIATELY before anything else
    const handleClose = (event) => {
      // CRITICAL: Stop ALL propagation FIRST before anything else can process
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      
      // Mark event so other handlers (like quick-add) know to ignore it
      event.personaliseDialogClick = true;
      event.cancelBubble = true;
      event.returnValue = false;
      
      
      // Call close method immediately
      component.closePersonaliseOnly();
      
      // Return false to ensure no other handlers run
      return false;
    };
    
    // Find ALL close buttons by multiple selectors
    const selectors = [
      '[data-personalise-close="true"]',
      '.personalise-modal__close',
      '.personalise-modal__cancel-button'
    ];
    
    let buttonsFound = 0;
    selectors.forEach(selector => {
      const buttons = this.querySelectorAll(selector);
      buttons.forEach(btn => {
        buttonsFound++;
        // Remove any existing handlers first to avoid duplicates
        btn.removeEventListener('click', handleClose, { capture: true });
        btn.removeEventListener('mousedown', handleClose, { capture: true });
        
        // Create wrapped handlers that ensure propagation is stopped IMMEDIATELY
        const wrappedMousedown = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          e.personaliseDialogClick = true;
          handleClose(e);
          return false;
        };
        
        const wrappedClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          e.personaliseDialogClick = true;
          handleClose(e);
          return false;
        };
        
        // Add handlers with capture phase FIRST to catch events BEFORE quick-add handlers
        btn.addEventListener('mousedown', wrappedMousedown, { capture: true, passive: false });
        btn.addEventListener('click', wrappedClick, { capture: true, passive: false });
      });
    });
    
    // ALSO set up delegation on dialog element as additional safety
    if (!dialogElement.dataset.personaliseCloseHandlerSetup) {
      const handleDelegatedClick = (event) => {
        const closeBtn = event.target.closest('[data-personalise-close="true"], .personalise-modal__close, .personalise-modal__cancel-button');
        if (closeBtn) {
          handleClose(event);
        }
      };
      
      dialogElement.addEventListener('click', handleDelegatedClick, { capture: true, passive: false });
      dialogElement.dataset.personaliseCloseHandlerSetup = 'true';
    }
  }
  
  /**
   * Sets the form attribute on Baby/Kid/Mum name inputs
   */
  #setFormAttributeOnInputs() {
    // Find the product form
    let productForm = null;
    const productFormComponent = this.closest('product-form-component');
    if (productFormComponent) {
      productForm = productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    // If not found, check if we're in a quick-add modal context
    if (!productForm) {
      const quickAddModal = document.querySelector('#quick-add-modal-content');
      if (quickAddModal) {
        productForm = quickAddModal.querySelector('form[data-type="add-to-cart-form"]');
      }
    }
    
    // Fallback to any form
    if (!productForm) {
      productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    if (productForm && productForm.id) {
      // Set form attribute on all inputs with data-form-id
      const inputs = this.querySelectorAll('[data-form-id]');
      inputs.forEach(input => {
        input.setAttribute('form', productForm.id);
      });
    }
  }
  
  /**
   * Sets up form submit listener for current session only
   */
  setupFormSubmitListenerForCurrentSession() {
    // Find the product form
    let productForm = null;
    const productFormComponent = this.closest('product-form-component');
    if (productFormComponent) {
      productForm = productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    // If not found, check if we're in a quick-add modal context
    if (!productForm) {
      const quickAddModal = document.querySelector('#quick-add-modal-content');
      if (quickAddModal) {
        productForm = quickAddModal.querySelector('form[data-type="add-to-cart-form"]');
      }
    }
    
    // Fallback to any form
    if (!productForm) {
      productForm = document.querySelector(`form[data-type="add-to-cart-form"]`);
    }
    if (productForm) {
      this.setupFormSubmitListener(productForm);
    }
  }

  /**
   * Returns which personalisation fields the current product supports, based on DOM presence.
   * Used to filter saved/loaded data so we never apply fields from one product to another.
   * @returns {Record<string, boolean>}
   */
  #getSupportedPersonalisationFields() {
    const root = this;
    return {
      name: !!(root.querySelector('#personalise-name') || root.querySelector('input[name="personalise-name"]')),
      font: !!(root.querySelector('.personalise-modal__font-grid') || root.refs?.fontGrid),
      color: !!(root.querySelector('.personalise-modal__color-grid') || root.refs?.colorGrid),
      dob: !!(root.querySelector('input[name="properties[Date of Birth]"]') || root.refs?.dobInput),
      schoolYear: !!(root.querySelector('input[name="properties[School Year]"]') || root.refs?.schoolYearInput),
      name1: !!(root.querySelector('input[name="properties[Name 1]"]') || root.refs?.name1Input),
      name2: !!(root.querySelector('input[name="properties[Name 2]"]') || root.refs?.name2Input),
      name3: !!(root.querySelector('input[name="properties[Name 3]"]') || root.refs?.name3Input),
      name4: !!(root.querySelector('input[name="properties[Name 4]"]') || root.refs?.name4Input),
      textbox: !!(root.querySelector('[name="properties[Personalisation:]"]') || root.refs?.textboxInput),
      message: !!(root.querySelector('textarea[name="properties[Message]"]') || root.refs?.messageInput),
      optionalDob: !!(root.querySelector('#dob_field_val') || root.querySelector('input[id="dob_field_val"]') || root.refs?.optionalDobInput),
      time: !!(root.querySelector('input[name="properties[Time]"]') || root.refs?.timeInput),
      weight: !!(root.querySelector('input[name="properties[Weight]"]') || root.refs?.weightInput),
      babyName: !!(root.querySelector('[data-name-input="baby"]') || root.refs?.babyNameInput),
      kidName: !!(root.querySelector('[data-name-input="kid"]') || root.refs?.kidNameInput),
      mumName: !!(root.querySelector('[data-name-input="mum"]') || root.refs?.mumNameInput)
    };
  }

  /**
   * Filters a personalisation object to only include fields the current product supports.
   * @param {Object} data - Personalisation data object
   * @returns {Object} Filtered object with only supported fields
   */
  #filterToSupportedFields(data) {
    if (!data) return {};
    const supported = this.#getSupportedPersonalisationFields();
    const filtered = {};
    for (const key of Object.keys(data)) {
      if (supported[key] && data[key] != null && (!(typeof data[key] === 'string') || data[key].trim() !== '')) {
        filtered[key] = data[key];
      }
    }
    return filtered;
  }

  /**
   * Loads saved personalisation from form inputs (not from storage)
   */
  loadSavedPersonalisation() {
    // First, check if we're opening from cart drawer (cart edit context)
    if (window.cartPersonalizationContext && window.cartPersonalizationContext.properties) {
      
      // Convert cart properties to personalisation data format
      const properties = window.cartPersonalizationContext.properties;
      const personalisation = {
        name: properties['Name'] || null,
        name1: properties['Name 1'] || null,
        name2: properties['Name 2'] || null,
        name3: properties['Name 3'] || null,
        name4: properties['Name 4'] || null,
        babyName: properties['Baby\'s Name'] || null,
        kidName: properties['Kid\'s Name'] || null,
        mumName: properties['Mum\'s Name'] || null,
        dob: properties['Date of Birth'] || null,
        optionalDob: properties['Personalise Date of Birth'] || null,
        schoolYear: properties['School Year'] || null,
        color: properties['Text Colour'] || null,
        font: properties['Text Font'] || null,
        textbox: properties['Personalisation:'] || null,
        message: properties['Message'] || null,
        time: properties['Time'] || null,
        weight: properties['Weight'] || null
      };
      
      // Filter out null/empty values and restrict to fields this product supports
      const filtered = this.#filterToSupportedFields(personalisation);
      this.personalisationData = {
        ...this.personalisationData,
        ...filtered
      };
      
      if (Object.keys(filtered).length > 0) {
        return; // Exit early, we have the data from cart
      }
    }
    
    // Find the product form - prefer form from same context as this dialog (avoids loading wrong product's data)
    let form = null;
    const forms = this.#getAllProductForms();
    if (forms.length > 0) {
      form = forms[0];
    }
    if (!form) {
      form = document.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    if (!form) {
      return; // No form found
    }
    
    // Read personalisation data directly from form inputs
    const personalisation = {};
    
    // Read name
    const nameInput = form.querySelector('input[name="properties[Name]"]');
    if (nameInput && nameInput.value.trim()) {
      personalisation.name = nameInput.value.trim();
    }
    
    // Read font
    const fontInput = form.querySelector('input[name="properties[Text Font]"]:checked') || 
                      form.querySelector('input[name="properties[Text Font]"]');
    if (fontInput && fontInput.value.trim()) {
      personalisation.font = fontInput.value.trim();
    }
    
    // Read color
    const colorInput = form.querySelector('input[name="properties[Text Colour]"]:checked') || 
                       form.querySelector('input[name="properties[Text Colour]"]');
    if (colorInput && colorInput.value.trim()) {
      personalisation.color = colorInput.value.trim();
    }
    
    // Read DOB (date picker submits yyyy-mm-dd; store as dd-mm-yyyy for line items)
    const dobInput = form.querySelector('input[name="properties[Date of Birth]"]');
    if (dobInput && dobInput.value.trim()) {
      personalisation.dob = this.#dateInputValueToDobProperty(dobInput.value.trim());
    }
    
    // Read school year
    const schoolYearInput = form.querySelector('input[name="properties[School Year]"]');
    if (schoolYearInput && schoolYearInput.value.trim()) {
      personalisation.schoolYear = schoolYearInput.value.trim();
    }
    
    // Read name1-4
    const name1Input = form.querySelector('input[name="properties[Name 1]"]');
    if (name1Input && name1Input.value.trim()) {
      personalisation.name1 = name1Input.value.trim();
    }
    
    const name2Input = form.querySelector('input[name="properties[Name 2]"]');
    if (name2Input && name2Input.value.trim()) {
      personalisation.name2 = name2Input.value.trim();
    }
    
    const name3Input = form.querySelector('input[name="properties[Name 3]"]');
    if (name3Input && name3Input.value.trim()) {
      personalisation.name3 = name3Input.value.trim();
    }
    
    const name4Input = form.querySelector('input[name="properties[Name 4]"]');
    if (name4Input && name4Input.value.trim()) {
      personalisation.name4 = name4Input.value.trim();
    }
    
    // Read textbox
    const textboxInput = form.querySelector('[name="properties[Personalisation:]"]');
    if (textboxInput && textboxInput.value.trim()) {
      personalisation.textbox = textboxInput.value.trim();
    }
    
    // Read message
    const messageInput = form.querySelector('textarea[name="properties[Message]"]');
    if (messageInput && messageInput.value.trim()) {
      personalisation.message = messageInput.value.trim();
    }
    
    // Read optional DOB (date picker or legacy name; normalize to tagged separator)
    let optionalDobInput = form.querySelector('input[name="properties[Personalise Date of Birth]"]');
    if (!optionalDobInput) {
      optionalDobInput = form.querySelector('#dob_field_val');
    }
    if (optionalDobInput && optionalDobInput.value.trim()) {
      const raw = optionalDobInput.value.trim();
      personalisation.optionalDob = optionalDobInput.type === 'date' || /^\d{4}-\d{2}-\d{2}$/.test(raw)
        ? this.#dateInputValueToDobProperty(raw)
        : this.#normalizeDobPropertyString(raw);
    }
    
    // Read time
    const timeInput = form.querySelector('input[name="properties[Time]"]');
    if (timeInput && timeInput.value.trim()) {
      personalisation.time = timeInput.value.trim();
    }
    
    // Read weight
    const weightInput = form.querySelector('input[name="properties[Weight]"]');
    if (weightInput && weightInput.value.trim()) {
      personalisation.weight = weightInput.value.trim();
    }
    
    // Read Baby/Kid/Mum names
    const babyNameInput = form.querySelector('input[name="properties[Baby\'s Name]"]');
    if (babyNameInput && babyNameInput.value.trim()) {
      personalisation.babyName = babyNameInput.value.trim();
    }
    
    const kidNameInput = form.querySelector('input[name="properties[Kid\'s Name]"]');
    if (kidNameInput && kidNameInput.value.trim()) {
      personalisation.kidName = kidNameInput.value.trim();
    }
    
    const mumNameInput = form.querySelector('input[name="properties[Mum\'s Name]"]');
    if (mumNameInput && mumNameInput.value.trim()) {
      personalisation.mumName = mumNameInput.value.trim();
    }
    
    // Merge with existing personalisationData to preserve structure
    this.personalisationData = {
      ...this.personalisationData,
      ...personalisation
    };
    
    // CRITICAL: If no personalisation found in form inputs, check window.currentPersonalisation as fallback
    // This is important because after adding to cart, form inputs might be cleared/morphed
    // but the data is still stored in window.currentPersonalisation
    if (Object.keys(personalisation).length === 0) {
      // Try to get product ID to look up stored personalisation
      let productId = null;
      const productFormComponent = form?.closest('product-form-component');
      if (productFormComponent) {
        productId = productFormComponent.dataset?.productId;
      }
      
      // If not found, try to get from quick-add modal
      if (!productId && quickAddModal) {
        const quickAddFormComponent = quickAddModal.querySelector('product-form-component');
        productId = quickAddFormComponent?.dataset?.productId;
      }
      
      // Check window.currentPersonalisation for this product
      if (productId && window.currentPersonalisation) {
        let savedPersonalisation = null;
        
        // Try to get personalisation by product ID first
        if (window.currentPersonalisation[productId]) {
          savedPersonalisation = { ...window.currentPersonalisation[productId] };
        } else if (window.currentPersonalisation._latest) {
          // Fallback to latest
          savedPersonalisation = { ...window.currentPersonalisation._latest };
        } else if (window.currentPersonalisation && typeof window.currentPersonalisation === 'object' && !Array.isArray(window.currentPersonalisation)) {
          // Check if it's a direct object (old format)
          const hasProductKeys = Object.keys(window.currentPersonalisation).some(key => key.startsWith('_') || /^\d+$/.test(key));
          if (!hasProductKeys) {
            savedPersonalisation = { ...window.currentPersonalisation };
          }
        }
        
        if (savedPersonalisation) {
          // Filter out null/empty values and internal keys
          Object.keys(savedPersonalisation).forEach(key => {
            if (key.startsWith('_') || 
                !savedPersonalisation[key] || 
                (typeof savedPersonalisation[key] === 'string' && !savedPersonalisation[key].trim())) {
              delete savedPersonalisation[key];
            }
          });
          // Only merge fields that THIS product supports (prevents Product A's font/color leaking to Product B)
          const filtered = this.#filterToSupportedFields(savedPersonalisation);
          if (Object.keys(filtered).length > 0) {
            this.personalisationData = {
              ...this.personalisationData,
              ...filtered
            };
          }
        }
      }
    }
    
  }

  /**
   * Shows the dialog.
   * Overridden to prevent click handlers from interfering with parent dialog
   * @param {string} [variantIdFromButton] - Optional variant ID captured when Personalise button was clicked (ensures we show the variant the user had selected)
   */
  showDialog(variantIdFromButton) {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Capture variant from form at open time so we can use it if form hasn't been updated yet by variant picker
    if (variantIdFromButton) {
      this._openingVariantId = String(variantIdFromButton);
    }

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Set up close button handlers BEFORE opening dialog
    // This ensures they're ready when the dialog opens
    this.#setupCloseButtonHandlers();

    // Add customize=true to URL when full-screen personalisation (product metafield custom.cb_full_screen_personalisation)
    // Only on product page - not when opened from quick-add or cart drawer
    if (this.dataset.fullScreenPersonalisation === 'true' && !this.hasAttribute('data-quick-add') && !window.cartPersonalizationContext) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('customize') !== 'true') {
        url.searchParams.set('customize', 'true');
        history.replaceState({}, '', url.toString());
        this._weAddedCustomizeParam = true;
      }
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      document.body.style.width = '100%';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());
      
      // Initialize save button state
      this.#initializeSaveButtonState();

      // Update overlay position on resize (dynamic personalization; position in px from visible image rect)
      if (this.dataset.cbPersonalizationImage) {
        if (!this._cbPreviewResizeHandler) {
          this._cbPreviewResizeHandler = () => {
            this.updateCbPreviewOverlay();
          };
        }
        window.addEventListener('resize', this._cbPreviewResizeHandler);
      }
      if (
        this.dataset.cbDobPersonalizationPosition ||
        this.dataset.cbTobPersonalizationPosition ||
        this.dataset.cbWeightPersonalizationPosition
      ) {
        this.#updateCbOptionalFieldPreviewOverlays();
      }

      // Only add keydown listener for Escape key, NOT click listener
      // This prevents the personalise dialog from closing parent dialog
      this.addEventListener('keydown', this.#handleKeyDownPersonalise);
      
      // Set up close button handlers again after dialog opens (ensure they're attached)
      // CRITICAL: Must set up handlers AFTER dialog is shown to catch events before quick-add handlers
      requestAnimationFrame(() => {
        this.#setupCloseButtonHandlers();
        // Try multiple times to ensure refs are populated and handlers are attached
        setTimeout(() => {
          this.#setupCloseButtonHandlers();
          // One more time after animation completes
          setTimeout(() => {
            this.#setupCloseButtonHandlers();
            // Final attempt after everything is fully loaded
            setTimeout(() => {
              this.#setupCloseButtonHandlers();
            }, 200);
          }, 200);
        }, 100);
      });
    });
    
    // Check if this is for build-your-set or quick-add modal - if so, clear state first
    // BUT don't clear if we're opening from cart drawer (cart edit context)
    const quickAddModal = document.getElementById('quick-add-modal-content');
    const isBuildYourSet = quickAddModal?.hasAttribute('data-build-your-set');
    const isCartContext = window.cartPersonalizationContext && window.cartPersonalizationContext.properties;
    const isQuickAddContext = quickAddModal && !isBuildYourSet;
    
    // Track the current product ID to detect when switching products
    const currentProductForm = quickAddModal?.querySelector('form[data-type="add-to-cart-form"]');
    const currentProductId = currentProductForm?.closest('product-form-component')?.dataset?.productId;
    const previousProductId = this._lastProductId;
    
    // Clear state if:
    // 1. It's build-your-set (and not cart context)
    // 2. It's a quick-add modal AND we're opening for a different product (product switched)
    // 3. It's a quick-add modal AND we don't have a previous product ID (first time opening)
    const shouldClearState = (isBuildYourSet && !isCartContext) || 
                             (isQuickAddContext && !isCartContext && (currentProductId !== previousProductId || !previousProductId));
    
    if (shouldClearState) {
      // Clear internal state to ensure fresh start for new product
      // But preserve cart context data if we're editing from cart
      this.selectedFont = null;
      this.selectedColor = null;
      this.personalisationData = {
        name: '',
        font: null,
        color: null,
        dob: null,
        schoolYear: null,
        name1: null,
        name2: null,
        name3: null,
        name4: null,
        textbox: null,
        message: null,
        optionalDob: null,
        time: null,
        weight: null,
        babyName: null,
        kidName: null,
        mumName: null
      };
      
      // CRITICAL: Also clear visual fields in the dialog to prevent showing old values
      // Wait for dialog to be available before clearing
      requestAnimationFrame(() => {
        // Clear name input
        const nameInput = this.refs.nameInput || this.querySelector('#personalise-name');
        if (nameInput) {
          nameInput.value = '';
          const charCounter = this.refs.charCounter?.querySelector('[ref="charCount"]') || this.refs.charCount;
          if (charCounter) {
            charCounter.textContent = '0';
          }
        }
        
        // Clear font selection
        if (this.refs.fontGrid) {
          const fontButtons = this.refs.fontGrid.querySelectorAll('.personalise-modal__font-button');
          fontButtons.forEach(btn => {
            btn.classList.remove('personalise-modal__font-button--selected');
          });
        }
        
        // Clear color selection
        const colorGrid = this.refs.colorGrid || this.querySelector('.personalise-modal__color-grid');
        if (colorGrid) {
          const colorRadios = colorGrid.querySelectorAll('input[type="radio"]');
          colorRadios.forEach(radio => {
            radio.checked = false;
          });
        }
        
        // Clear Baby/Kid/Mum name inputs
        const babyNameInput = this.refs.babyNameInput || this.querySelector('[data-name-input="baby"]');
        if (babyNameInput) {
          babyNameInput.value = '';
          this.updateNameTabCharCounter('baby', 0);
          this.updateNameTabIcon('baby', false);
        }
        
        const kidNameInput = this.refs.kidNameInput || this.querySelector('[data-name-input="kid"]');
        if (kidNameInput) {
          kidNameInput.value = '';
          this.updateNameTabCharCounter('kid', 0);
          this.updateNameTabIcon('kid', false);
        }
        
        const mumNameInput = this.refs.mumNameInput || this.querySelector('[data-name-input="mum"]');
        if (mumNameInput) {
          mumNameInput.value = '';
          this.updateNameTabCharCounter('mum', 0);
          this.updateNameTabIcon('mum', false);
        }
        
        // Clear all other input fields
        const allInputs = this.querySelectorAll('input[type="text"], textarea, input[type="date"]');
        allInputs.forEach(input => {
          // Skip radio buttons and checkboxes - they're handled separately
          if (input.type !== 'radio' && input.type !== 'checkbox') {
            input.value = '';
          }
        });
      });
    }
    
    // Store current product ID for next comparison
    if (currentProductId) {
      this._lastProductId = currentProductId;
    }
    
    // Load saved personalisation from current session only (non-blocking)
    // This will check cart context first, then form inputs, then window.currentPersonalisation
    // BUT: If we just cleared state for a new product, we still want to load from the form
    // (which should be empty for the new product, or have its own saved values)
    try {
      this.loadSavedPersonalisation();
      
      // After loading, if we got data from window.currentPersonalisation (not from form),
      // re-add the hidden inputs to the form so they're available for next add-to-cart
      if (this.personalisationData && Object.keys(this.personalisationData).length > 0) {
        // Check if form has the inputs - if not, we loaded from window.currentPersonalisation
        const form = quickAddModal?.querySelector('form[data-type="add-to-cart-form"]') ||
                    this.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]') ||
                    document.querySelector('form[data-type="add-to-cart-form"]');
        
        if (form) {
          const hasNameInput = form.querySelector('input[name="properties[Name]"]');
          const hasFontInput = form.querySelector('input[name="properties[Text Font]"]');
          const hasColorInput = form.querySelector('input[name="properties[Text Colour]"]');
          
          // If form doesn't have personalization inputs but we have data, re-add them
          if ((this.personalisationData.name && !hasNameInput) ||
              (this.personalisationData.font && !hasFontInput) ||
              (this.personalisationData.color && !hasColorInput)) {
            // Re-add inputs to form using the updateFormFields method
            this.updateFormFields(this.personalisationData);
          }
        }
      }
    } catch (error) {
      console.error('Error loading personalisation:', error);
    }
    
    // Wait for dialog to be fully open and refs to be available
    let attempts = 0;
    const maxAttempts = 20; // Maximum 1 second (20 * 50ms)
    
    const checkAndPopulate = () => {
      attempts++;
      const dialog = this.refs.dialog;
      
      if (dialog && dialog.open) {
        // Dialog is open, try to populate fields
        // Use requestAnimationFrame to ensure DOM is ready
        requestAnimationFrame(() => {
          try {
            // Sync variant/color grid FIRST so we have correct colors before populating (fixes wrong variant colors on Edit after variant change)
            this.#syncVariantAndPreviewOnOpen();
            this.#populateFieldsFromSavedData();
            // Set up input listeners after fields are populated
            this.#attachInputListeners();
            // Sync again after populate (populate may have triggered DOM changes)
            this.#syncVariantAndPreviewOnOpen();
            // Select first color and font by default if none selected
            this.#selectFirstColorAndFontIfNotSelected();
            // Update dynamic personalization text overlay if variant/product has cb metafields
            this.updateCbPreviewOverlay();
            // Retry sync at intervals (variant picker updates form async after morph - this catches the update)
            setTimeout(() => {
              this.#syncVariantAndPreviewOnOpen();
              this.#selectFirstColorAndFontIfNotSelected();
            }, 150);
            setTimeout(() => {
              this.#syncVariantAndPreviewOnOpen();
              this.#selectFirstColorAndFontIfNotSelected();
            }, 350);
            setTimeout(() => {
              this.#syncVariantAndPreviewOnOpen();
              this._openingVariantId = null; // clear after last retry
              this.#selectFirstColorAndFontIfNotSelected(); // after grid may have been rebuilt
            }, 550);
          } catch (error) {
            console.error('Error populating fields:', error);
          }
        });
      } else if (attempts < maxAttempts) {
        // Dialog not open yet, try again
        setTimeout(checkAndPopulate, 50);
      } else {
        // Max attempts reached, try to populate anyway
        try {
          this.#populateFieldsFromSavedData();
          this.#attachInputListeners();
          this.#syncVariantAndPreviewOnOpen();
          this.#selectFirstColorAndFontIfNotSelected();
          this.updateCbPreviewOverlay();
        } catch (error) {
          console.error('Error populating fields after max attempts:', error);
        }
      }
    };
    
    // Start checking after a small delay to let the dialog open
    setTimeout(checkAndPopulate, 100);
  }

  /**
   * Handle Escape key for personalise dialog only
   * @private
   */
  #handleKeyDownPersonalise = (event) => {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    this.closePersonaliseDialog();
  };

  /**
   * COMPLETELY SEPARATE CLOSE METHOD - bypasses ALL parent logic
   * Only called by buttons with data-personalise-close="true"
   * This method name is unique and won't conflict with anything
   */
  closePersonaliseOnly = async () => {
    this._openingVariantId = null; // clear so next open doesn't use stale variant
    if (this._cbPreviewResizeHandler) {
      window.removeEventListener('resize', this._cbPreviewResizeHandler);
    }

    // Clear internal state when closing (especially for build-your-set to prevent font persistence)
    this.selectedFont = null;
    this.selectedColor = null;
    // Reset personalisationData to initial state
    this.personalisationData = {
      name: '',
      font: null,
      color: null,
      dob: null,
      schoolYear: null,
      name1: null,
      name2: null,
      name3: null,
      name4: null,
      textbox: null,
      message: null,
      optionalDob: null,
      time: null,
      weight: null,
      babyName: null,
      kidName: null,
      mumName: null
    };
    
    // Get dialog directly - don't rely on refs if they might be stale
    const dialogElement = this.querySelector('dialog') || this.refs?.dialog;

    if (!dialogElement) {
      // Even if dialog isn't open, ensure body styles are reset if no other dialogs are open
      const quickAddDialog = document.querySelector('#quick-add-dialog dialog');
      const quickAddIsOpen = quickAddDialog && quickAddDialog.open;
      if (!quickAddIsOpen) {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
      }
      return;
    }
    
    if (!dialogElement.open) {
      // Even if dialog isn't open, ensure body styles are reset if no other dialogs are open
      const quickAddDialog = document.querySelector('#quick-add-dialog dialog');
      const quickAddIsOpen = quickAddDialog && quickAddDialog.open;
      if (!quickAddIsOpen) {
        document.body.style.width = '';
        document.body.style.position = '';
        document.body.style.top = '';
      }
      return;
    }

    // Remove keydown listener
    try {
      this.removeEventListener('keydown', this.#handleKeyDownPersonalise);
    } catch (e) {
      // Ignore
    }

    dialogElement.classList.add('dialog-closing');

    try {
      await onAnimationEnd(dialogElement, undefined, {
        subtree: false,
      });
    } catch (e) {
      console.warn('Animation end error:', e);
    }

    // Close this dialog FIRST - don't check anything else
    try {
      dialogElement.close();
    } catch (e) {
      console.error('Error calling dialog.close():', e);
    }
    
    dialogElement.classList.remove('dialog-closing');

    // Check if quick-add dialog is still open AFTER closing this one
    const quickAddDialog = document.querySelector('#quick-add-dialog dialog');
    const quickAddIsOpen = quickAddDialog && quickAddDialog.open;
    
    // Only reset body styles if quick-add dialog is also closed
    if (!quickAddIsOpen) {
      document.body.style.width = '';
      document.body.style.position = '';
      document.body.style.top = '';
      // Restore scroll position using saved value
      window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
    }

    // Remove customize=true from URL when closing (product page only, not quick-add/cart)
    if (this.dataset.context !== 'cart-drawer' && !this.hasAttribute('data-quick-add')) {
      const url = new URL(window.location.href);
      if (url.searchParams.get('customize') === 'true') {
        url.searchParams.delete('customize');
        history.replaceState({}, '', url.toString());
      }
    }
    this._weAddedCustomizeParam = false;

    try {
      this.dispatchEvent(new DialogCloseEvent());
    } catch (e) {
      console.error('Error dispatching event:', e);
    }
  };

  /**
   * Keep old method names for backwards compatibility but delegate to new method
   */
  handleCloseButton = async (event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
    }
    await this.closePersonaliseOnly();
  };

  closePersonaliseDialog = async () => {
    return this.closePersonaliseOnly();
  };

  /**
   * Override closeDialog to use our custom implementation
   * This prevents parent dialog checks from interfering
   */
  closeDialog = async () => {
    return this.closePersonaliseOnly();
  };
  
  /**
   * Populates all form fields from saved personalisation data
   * @private
   */
  #populateFieldsFromSavedData() {
    // Only populate if we have data (from current session)
    // Check if ANY personalization field has data, not just name/font/color
    if (!this.personalisationData) {
      return; // No data to populate
    }
    
    // Check if any personalization field has data
    const hasAnyData = 
      this.personalisationData.name ||
      this.personalisationData.font ||
      this.personalisationData.color ||
      this.personalisationData.dob ||
      this.personalisationData.optionalDob ||
      this.personalisationData.schoolYear ||
      this.personalisationData.name1 ||
      this.personalisationData.name2 ||
      this.personalisationData.name3 ||
      this.personalisationData.name4 ||
      this.personalisationData.textbox ||
      this.personalisationData.message ||
      this.personalisationData.time ||
      this.personalisationData.weight ||
      this.personalisationData.babyName ||
      this.personalisationData.kidName ||
      this.personalisationData.mumName;
    
    if (!hasAnyData) {
      return; // No data to populate
    }
    
    // Update name input field
    const nameInput = this.refs.nameInput || this.querySelector('#personalise-name');
    if (nameInput && this.personalisationData.name) {
      nameInput.value = this.personalisationData.name;
      // Trigger input event to update character counter
      nameInput.dispatchEvent(new Event('input', { bubbles: true }));
      const charCounter = this.refs.charCounter?.querySelector('[ref="charCount"]') || this.refs.charCount;
      if (charCounter) {
        charCounter.textContent = this.personalisationData.name.length;
      }
    }
    
    // Set font selection if saved
    if (this.personalisationData.font) {
      setTimeout(() => {
        this.selectFontByName(this.personalisationData.font);
      }, 100);
    }
    
    // Set color selection if saved (run immediately and retry after sync - #syncVariantAndPreviewOnOpen may rebuild grid)
    const colorToApply = this.personalisationData.color;
    if (colorToApply) {
      this.selectColorByName(colorToApply);
      setTimeout(() => this.selectColorByName(colorToApply), 100);
      setTimeout(() => this.selectColorByName(colorToApply), 350);
    }
    
    // Load other fields
    const dobInput = this.refs.dobInput || this.querySelector('input[name="properties[Date of Birth]"]');
    if (dobInput && this.personalisationData.dob) {
      const forDateInput = this.#dobPropertyToDateInputValue(this.personalisationData.dob);
      if (forDateInput) dobInput.value = forDateInput;
    }
    
    const schoolYearInput = this.refs.schoolYearInput || this.querySelector('input[name="properties[School Year]"]');
    if (schoolYearInput && this.personalisationData.schoolYear) schoolYearInput.value = this.personalisationData.schoolYear;
    
    const name1Input = this.refs.name1Input || this.querySelector('input[name="properties[Name 1]"]');
    if (name1Input && this.personalisationData.name1) name1Input.value = this.personalisationData.name1;
    
    const name2Input = this.refs.name2Input || this.querySelector('input[name="properties[Name 2]"]');
    if (name2Input && this.personalisationData.name2) name2Input.value = this.personalisationData.name2;
    
    const name3Input = this.refs.name3Input || this.querySelector('input[name="properties[Name 3]"]');
    if (name3Input && this.personalisationData.name3) name3Input.value = this.personalisationData.name3;
    
    const name4Input = this.refs.name4Input || this.querySelector('input[name="properties[Name 4]"]');
    if (name4Input && this.personalisationData.name4) name4Input.value = this.personalisationData.name4;
    
    const textboxInput = this.refs.textboxInput || this.querySelector('[name="properties[Personalisation:]"]');
    if (textboxInput && this.personalisationData.textbox) textboxInput.value = this.personalisationData.textbox;
    
    const messageInput = this.refs.messageInput || this.querySelector('textarea[name="properties[Message]"]');
    if (messageInput && this.personalisationData.message) messageInput.value = this.personalisationData.message;
    
    const optionalDobInput = this.refs.optionalDobInput || this.querySelector('#dob_field_val');
    if (optionalDobInput && this.personalisationData.optionalDob) {
      const forDateInput = this.#dobPropertyToDateInputValue(this.personalisationData.optionalDob);
      if (forDateInput) optionalDobInput.value = forDateInput;
    }
    
    const timeInput = this.refs.timeInput || this.querySelector('input[name="properties[Time]"]');
    if (timeInput && this.personalisationData.time) timeInput.value = this.personalisationData.time;
    
    const weightInput = this.refs.weightInput || this.querySelector('input[name="properties[Weight]"]');
    if (weightInput && this.personalisationData.weight) weightInput.value = this.personalisationData.weight;
    
    // Populate Baby/Kid/Mum name inputs
    const babyNameInput = this.refs.babyNameInput || this.querySelector('[data-name-input="baby"]');
    if (babyNameInput && this.personalisationData.babyName) {
      babyNameInput.value = this.personalisationData.babyName;
      // Update character counter
      this.updateNameTabCharCounter('baby', babyNameInput.value.length);
      // Update icon after DOM is ready - retry up to 5 times
      let attempts = 0;
      const maxAttempts = 5;
      const updateBabyIcon = () => {
        attempts++;
        const hasValue = babyNameInput.value.trim().length > 0;
        const icon = this.querySelector(`[data-tab-icon="baby"]`);
        if (icon) {
          this.updateNameTabIcon('baby', hasValue);
        } else if (attempts < maxAttempts) {
          setTimeout(updateBabyIcon, 100);
        }
      };
      setTimeout(updateBabyIcon, 100);
    }
    
    const kidNameInput = this.refs.kidNameInput || this.querySelector('[data-name-input="kid"]');
    if (kidNameInput && this.personalisationData.kidName) {
      kidNameInput.value = this.personalisationData.kidName;
      // Update character counter
      this.updateNameTabCharCounter('kid', kidNameInput.value.length);
      // Update icon after DOM is ready - retry up to 5 times
      let attempts = 0;
      const maxAttempts = 5;
      const updateKidIcon = () => {
        attempts++;
        const hasValue = kidNameInput.value.trim().length > 0;
        const icon = this.querySelector(`[data-tab-icon="kid"]`);
        if (icon) {
          this.updateNameTabIcon('kid', hasValue);
        } else if (attempts < maxAttempts) {
          setTimeout(updateKidIcon, 100);
        }
      };
      setTimeout(updateKidIcon, 100);
    }
    
    const mumNameInput = this.refs.mumNameInput || this.querySelector('[data-name-input="mum"]');
    if (mumNameInput && this.personalisationData.mumName) {
      mumNameInput.value = this.personalisationData.mumName;
      // Update character counter
      this.updateNameTabCharCounter('mum', mumNameInput.value.length);
      // Update icon after DOM is ready - retry up to 5 times
      let attempts = 0;
      const maxAttempts = 5;
      const updateMumIcon = () => {
        attempts++;
        const hasValue = mumNameInput.value.trim().length > 0;
        const icon = this.querySelector(`[data-tab-icon="mum"]`);
        if (icon) {
          this.updateNameTabIcon('mum', hasValue);
        } else if (attempts < maxAttempts) {
          setTimeout(updateMumIcon, 100);
        }
      };
      setTimeout(updateMumIcon, 100);
    }
    
    this.updateSaveButton();
  }
  
  /**
   * Selects first color and first font by default when modal opens, if none are selected.
   * @private
   */
  #selectFirstColorAndFontIfNotSelected() {
    const colorGrid = this.refs?.colorGrid || this.querySelector('.personalise-modal__color-grid');
    const fontGrid = this.refs?.fontGrid || this.querySelector('.personalise-modal__font-grid');

    if (colorGrid) {
      const hasColor = this.selectedColor || this.personalisationData?.color ||
        Array.from(colorGrid.querySelectorAll('input[type="radio"]')).some((input) => input.checked);
      if (!hasColor) {
        const firstRadio = colorGrid.querySelector('input[type="radio"]');
        const firstValue = firstRadio?.value || firstRadio?.closest('.personalise-modal__color-button')?.dataset?.color;
        if (firstValue) this.selectColorByName(firstValue);
      }
    }

    if (fontGrid) {
      const hasFont = this.selectedFont || this.personalisationData?.font;
      if (!hasFont) {
        const firstButton = fontGrid.querySelector('.personalise-modal__font-button');
        const firstFont = firstButton?.dataset?.font;
        if (firstFont) this.selectFontByName(firstFont);
      }
    }
  }

  /**
   * Initializes save button state when dialog opens
   * @private
   */
  #initializeSaveButtonState() {
    // Wait a bit for DOM to be ready
    setTimeout(() => {
      this.updateSaveButton();
    }, 100);
  }

  /**
   * '-' (default) or '.' from product tags dob_separated_by_dot / dob_separated_by_dash.
   * @returns {'.'|'-'}
   */
  #getDobSeparator() {
    return this.dataset.dobSeparator === '.' ? '.' : '-';
  }

  /**
   * @param {string} stored
   * @returns {{ dd: string, mm: string, yyyy: string } | null}
   */
  #parseDobStringToParts(stored) {
    const v = String(stored || '').trim();
    if (!v) return null;
    let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (m) {
      return { dd: m[3], mm: m[2], yyyy: m[1] };
    }
    m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(v);
    if (m) {
      return { dd: m[1], mm: m[2], yyyy: m[3] };
    }
    m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(v);
    if (m) {
      return { dd: m[1], mm: m[2], yyyy: m[3] };
    }
    return null;
  }

  /** @param {string} dd @param {string} mm @param {string} yyyy */
  #formatDobPropertyFromParts(dd, mm, yyyy) {
    const sep = this.#getDobSeparator();
    return `${dd}${sep}${mm}${sep}${yyyy}`;
  }

  /**
   * HTML date input (yyyy-mm-dd) → line item string using current product separator.
   * @param {string} inputValue
   * @returns {string}
   */
  #dateInputValueToDobProperty(inputValue) {
    const v = String(inputValue || '').trim();
    if (!v) return '';
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
    if (m) {
      return this.#formatDobPropertyFromParts(m[3], m[2], m[1]);
    }
    const parts = this.#parseDobStringToParts(v);
    if (parts) return this.#formatDobPropertyFromParts(parts.dd, parts.mm, parts.yyyy);
    return v;
  }

  /**
   * @param {string} stored - dd-mm-yyyy, dd.mm.yyyy, or yyyy-mm-dd
   * @returns {string} yyyy-mm-dd for date input, or '' if not parseable
   */
  #dobPropertyToDateInputValue(stored) {
    const parts = this.#parseDobStringToParts(stored);
    if (!parts) return '';
    return `${parts.yyyy}-${parts.mm}-${parts.dd}`;
  }

  /**
   * Re-format a stored DOB to the current product separator (preview / consistency).
   * @param {string} stored
   * @returns {string}
   */
  #normalizeDobPropertyString(stored) {
    const parts = this.#parseDobStringToParts(stored);
    if (!parts) return String(stored || '').trim();
    return this.#formatDobPropertyFromParts(parts.dd, parts.mm, parts.yyyy);
  }

  /**
   * Refreshes DOB, time-of-birth, and weight preview overlays on the dynamic image.
   * @private
   */
  #updateCbOptionalFieldPreviewOverlays() {
    this.updateDobPreviewOverlay();
    this.updateTobPreviewOverlay();
    this.updateWeightPreviewOverlay();
  }

  /**
   * Updates the dynamic personalization text overlay on the preview image when
   * variant/product has cb_personalization_image and cb_personalization_position metafields.
   * Positions overlay in pixels from the visible (object-fit: contain) image rect so the
   * image stays full size and text placement is correct at all viewports.
   */
  updateCbPreviewOverlay() {
    const imageUrl = this.dataset.cbPersonalizationImage;
    const positionJson = this.dataset.cbPersonalizationPosition;

    const overlay = (this.refs && this.refs.previewTextOverlay) || this.querySelector('.personalise-modal__preview-text-overlay');

    // When no position (e.g. variant has image but no position), clear overlay so previous variant's text doesn't persist
    if (!imageUrl || !positionJson) {
      if (overlay) overlay.textContent = '';
      this.#updateCbOptionalFieldPreviewOverlays();
      return;
    }
    if (!overlay) {
      this.#updateCbOptionalFieldPreviewOverlays();
      return;
    }

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const img = wrap && wrap.querySelector('.personalise-modal__preview-image--dynamic');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.updateCbPreviewOverlay(), { once: true });
    }

    let position;
    try {
      position = typeof positionJson === 'string' ? JSON.parse(positionJson) : positionJson;
    } catch (e) {
      this.#updateCbOptionalFieldPreviewOverlays();
      return;
    }
    const xPct = position.x != null ? Number(position.x) : 20;
    const yPct = position.y != null ? Number(position.y) : 20;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const fontSize = (isMobile && position.font_size_mobile != null) ? Number(position.font_size_mobile) : (position.font_size != null ? Number(position.font_size) : 20);

    const supported = this.#getSupportedPersonalisationFields();
    let name = '';
    if (supported.babyName || supported.kidName || supported.mumName) {
      const activePanel = this.querySelector('.personalise-name-tabs__panel.is-active');
      const panelType = activePanel && activePanel.dataset.panel;
      if (panelType === 'baby') {
        const babyInput = (this.refs && this.refs.babyNameInput) || this.querySelector('[data-name-input="baby"]');
        name = (babyInput && babyInput.value) || (this.personalisationData && this.personalisationData.babyName) || '';
      } else if (panelType === 'kid') {
        const kidInput = (this.refs && this.refs.kidNameInput) || this.querySelector('[data-name-input="kid"]');
        name = (kidInput && kidInput.value) || (this.personalisationData && this.personalisationData.kidName) || '';
      } else if (panelType === 'mum') {
        const mumInput = (this.refs && this.refs.mumNameInput) || this.querySelector('[data-name-input="mum"]');
        name = (mumInput && mumInput.value) || (this.personalisationData && this.personalisationData.mumName) || '';
      }
      name = String(name).trim();
    } else {
      const nameInput = (this.refs && this.refs.nameInput) || this.querySelector('#personalise-name');
      const nameVal = (nameInput && nameInput.value) || (this.personalisationData && this.personalisationData.name) || '';
      name = String(nameVal).trim();
    }
    const colorName = supported.color ? ((this.personalisationData && this.personalisationData.color) || this.selectedColor || '') : '';
    const fontName = supported.font ? ((this.personalisationData && this.personalisationData.font) || this.selectedFont || '') : '';

    overlay.textContent = name;
    overlay.style.fontSize = `${fontSize}px`;
    overlay.style.fontFamily = fontName ? `"${fontName}", sans-serif` : '';
    overlay.style.transform = 'translate(-50%, 50%)';
    overlay.style.top = '';

    const colorMap = {
      black: '#000000',
      white: '#ffffff',
      blue: '#DEE8EF',
      gold: '#DEB035',
      green: '#E4EFDB',
      grey: '#E8EBEC',
      gray: '#E8EBEC',
      multicolour: '#8B4789',
      orange: '#F8CF89',
      pink: '#F7DDE2',
      purple: '#F0D9E6',
      red: '#BC3725',
      silver: '#DEEBF7',
      yellow: '#F9F3DB'
    };
    const colorKey = colorName && colorName.toLowerCase ? colorName.toLowerCase() : '';
    const cssColor = (colorKey && colorMap[colorKey]) || colorName || '';
    overlay.style.color = (supported.color && cssColor) ? cssColor : 'inherit';

    if (!wrap || !img || !img.naturalWidth) {
      this.#updateCbOptionalFieldPreviewOverlays();
      return;
    }
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) {
      this.#updateCbOptionalFieldPreviewOverlays();
      return;
    }
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const scale = Math.min(wrapW / nw, wrapH / nh);
    const vW = nw * scale;
    const vH = nh * scale;
    const vL = (wrapW - vW) / 2;
    const vT = (wrapH - vH) / 2;
    const leftPx = vL + (vW * xPct / 100);
    const bottomPx = wrapH - vT - (vH * (1 - yPct / 100));
    overlay.style.left = Math.round(leftPx) + 'px';
    overlay.style.bottom = Math.round(bottomPx) + 'px';
    this.#updateCbOptionalFieldPreviewOverlays();
  }

  /**
   * Updates the DOB preview overlay on the dynamic preview image when the product has
   * cb_dob_personalization_poisition metafield (variant or product). Positions overlay in pixels
   * from the visible (object-fit: contain) image rect.
   */
  updateDobPreviewOverlay() {
    const positionJson = this.dataset.cbDobPersonalizationPosition;
    if (!positionJson) {
      const dobOverlay = (this.refs && this.refs.previewDobOverlay) || this.querySelector('.personalise-modal__preview-dob-overlay');
      if (dobOverlay) {
        dobOverlay.style.display = 'none';
        dobOverlay.textContent = '';
      }
      return;
    }

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const img = wrap && wrap.querySelector('.personalise-modal__preview-image--dynamic');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.updateDobPreviewOverlay(), { once: true });
    }

    const dobOverlay = (this.refs && this.refs.previewDobOverlay) || this.querySelector('.personalise-modal__preview-dob-overlay');
    if (!dobOverlay) return;

    let position;
    try {
      position = typeof positionJson === 'string' ? JSON.parse(positionJson) : positionJson;
    } catch (e) {
      dobOverlay.style.display = 'none';
      return;
    }

    // Get DOB value from either personalized_dob or optional_fields input
    const dobInput = this.refs?.dobInput || this.querySelector('input[name="properties[Date of Birth]"]');
    const optionalDobInput = this.refs?.optionalDobInput || this.querySelector('#dob_field_val');
    let dobValue = '';
    if (dobInput?.value?.trim()) {
      dobValue = this.#dateInputValueToDobProperty(dobInput.value.trim());
    } else if (optionalDobInput?.value) {
      dobValue = this.#dateInputValueToDobProperty(optionalDobInput.value);
    } else if (this.personalisationData?.dob) {
      dobValue = this.#normalizeDobPropertyString(this.personalisationData.dob);
    } else if (this.personalisationData?.optionalDob) {
      dobValue = this.#normalizeDobPropertyString(this.personalisationData.optionalDob);
    }

    if (!dobValue) {
      dobOverlay.style.display = 'none';
      dobOverlay.textContent = '';
      return;
    }

    const xPct = position.x != null ? Number(position.x) : 20;
    const yPct = position.y != null ? Number(position.y) : 20;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const fontSize = (isMobile && position.font_size_mobile != null) ? Number(position.font_size_mobile) : (position.font_size != null ? Number(position.font_size) : 20);

    const supported = this.#getSupportedPersonalisationFields();
    const colorName = supported.color ? ((this.personalisationData && this.personalisationData.color) || this.selectedColor || '') : '';
    const fontName = supported.font ? ((this.personalisationData && this.personalisationData.font) || this.selectedFont || '') : '';

    const colorMap = {
      black: '#000000',
      white: '#ffffff',
      blue: '#DEE8EF',
      gold: '#DEB035',
      green: '#E4EFDB',
      grey: '#E8EBEC',
      gray: '#E8EBEC',
      multicolour: '#8B4789',
      orange: '#F8CF89',
      pink: '#F7DDE2',
      purple: '#F0D9E6',
      red: '#BC3725',
      silver: '#DEEBF7',
      yellow: '#F9F3DB'
    };
    const colorKey = colorName && colorName.toLowerCase ? colorName.toLowerCase() : '';
    const cssColor = (colorKey && colorMap[colorKey]) || colorName || '';

    dobOverlay.textContent = dobValue;
    dobOverlay.style.fontSize = `${fontSize}px`;
    dobOverlay.style.fontFamily = fontName ? `"${fontName}", sans-serif` : '';
    dobOverlay.style.transform = 'translate(-50%, 50%)';
    dobOverlay.style.top = '';
    dobOverlay.style.color = (supported.color && cssColor) ? cssColor : 'inherit';
    dobOverlay.style.display = '';

    if (!wrap || !img || !img.naturalWidth) return;
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const scale = Math.min(wrapW / nw, wrapH / nh);
    const vW = nw * scale;
    const vH = nh * scale;
    const vL = (wrapW - vW) / 2;
    const vT = (wrapH - vH) / 2;
    const leftPx = vL + (vW * xPct / 100);
    const bottomPx = wrapH - vT - (vH * (1 - yPct / 100));
    dobOverlay.style.left = Math.round(leftPx) + 'px';
    dobOverlay.style.bottom = Math.round(bottomPx) + 'px';
  }

  /**
   * Time of birth preview overlay (custom.cb_tob_personalization_poisition). Same coordinate logic as DOB.
   */
  updateTobPreviewOverlay() {
    const positionJson = this.dataset.cbTobPersonalizationPosition;
    if (!positionJson) {
      const el = (this.refs && this.refs.previewTobOverlay) || this.querySelector('.personalise-modal__preview-tob-overlay');
      if (el) {
        el.style.display = 'none';
        el.textContent = '';
      }
      return;
    }

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const img = wrap && wrap.querySelector('.personalise-modal__preview-image--dynamic');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.updateTobPreviewOverlay(), { once: true });
    }

    const tobOverlay = (this.refs && this.refs.previewTobOverlay) || this.querySelector('.personalise-modal__preview-tob-overlay');
    if (!tobOverlay) return;

    let position;
    try {
      position = typeof positionJson === 'string' ? JSON.parse(positionJson) : positionJson;
    } catch (e) {
      tobOverlay.style.display = 'none';
      return;
    }

    const timeInput = this.refs?.timeInput || this.querySelector('input[name="properties[Time]"]');
    let timeValue =
      (timeInput?.value && String(timeInput.value).trim()) ||
      (this.personalisationData?.time ? String(this.personalisationData.time).trim() : '');

    if (!timeValue) {
      tobOverlay.style.display = 'none';
      tobOverlay.textContent = '';
      return;
    }

    const xPct = position.x != null ? Number(position.x) : 20;
    const yPct = position.y != null ? Number(position.y) : 20;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const fontSize = (isMobile && position.font_size_mobile != null) ? Number(position.font_size_mobile) : (position.font_size != null ? Number(position.font_size) : 20);

    const supported = this.#getSupportedPersonalisationFields();
    const colorName = supported.color ? ((this.personalisationData && this.personalisationData.color) || this.selectedColor || '') : '';
    const fontName = supported.font ? ((this.personalisationData && this.personalisationData.font) || this.selectedFont || '') : '';

    const colorMap = {
      black: '#000000',
      white: '#ffffff',
      blue: '#DEE8EF',
      gold: '#DEB035',
      green: '#E4EFDB',
      grey: '#E8EBEC',
      gray: '#E8EBEC',
      multicolour: '#8B4789',
      orange: '#F8CF89',
      pink: '#F7DDE2',
      purple: '#F0D9E6',
      red: '#BC3725',
      silver: '#DEEBF7',
      yellow: '#F9F3DB'
    };
    const colorKey = colorName && colorName.toLowerCase ? colorName.toLowerCase() : '';
    const cssColor = (colorKey && colorMap[colorKey]) || colorName || '';

    tobOverlay.textContent = timeValue;
    tobOverlay.style.fontSize = `${fontSize}px`;
    tobOverlay.style.fontFamily = fontName ? `"${fontName}", sans-serif` : '';
    tobOverlay.style.transform = 'translate(-50%, 50%)';
    tobOverlay.style.top = '';
    tobOverlay.style.color = (supported.color && cssColor) ? cssColor : 'inherit';
    tobOverlay.style.display = '';

    if (!wrap || !img || !img.naturalWidth) return;
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const scale = Math.min(wrapW / nw, wrapH / nh);
    const vW = nw * scale;
    const vH = nh * scale;
    const vL = (wrapW - vW) / 2;
    const vT = (wrapH - vH) / 2;
    const leftPx = vL + (vW * xPct / 100);
    const bottomPx = wrapH - vT - (vH * (1 - yPct / 100));
    tobOverlay.style.left = Math.round(leftPx) + 'px';
    tobOverlay.style.bottom = Math.round(bottomPx) + 'px';
  }

  /**
   * Weight preview overlay (custom.cb_weight_personalization_poisition). Same coordinate logic as DOB.
   */
  updateWeightPreviewOverlay() {
    const positionJson = this.dataset.cbWeightPersonalizationPosition;
    if (!positionJson) {
      const el = (this.refs && this.refs.previewWeightOverlay) || this.querySelector('.personalise-modal__preview-weight-overlay');
      if (el) {
        el.style.display = 'none';
        el.textContent = '';
      }
      return;
    }

    const wrap = this.querySelector('.personalise-modal__preview-image-wrap');
    const img = wrap && wrap.querySelector('.personalise-modal__preview-image--dynamic');
    if (img && !img.complete) {
      img.addEventListener('load', () => this.updateWeightPreviewOverlay(), { once: true });
    }

    const weightOverlay = (this.refs && this.refs.previewWeightOverlay) || this.querySelector('.personalise-modal__preview-weight-overlay');
    if (!weightOverlay) return;

    let position;
    try {
      position = typeof positionJson === 'string' ? JSON.parse(positionJson) : positionJson;
    } catch (e) {
      weightOverlay.style.display = 'none';
      return;
    }

    const weightInput = this.refs?.weightInput || this.querySelector('input[name="properties[Weight]"]');
    let weightValue =
      (weightInput?.value && String(weightInput.value).trim()) ||
      (this.personalisationData?.weight ? String(this.personalisationData.weight).trim() : '');

    if (!weightValue) {
      weightOverlay.style.display = 'none';
      weightOverlay.textContent = '';
      return;
    }

    const xPct = position.x != null ? Number(position.x) : 20;
    const yPct = position.y != null ? Number(position.y) : 20;
    const isMobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
    const fontSize = (isMobile && position.font_size_mobile != null) ? Number(position.font_size_mobile) : (position.font_size != null ? Number(position.font_size) : 20);

    const supported = this.#getSupportedPersonalisationFields();
    const colorName = supported.color ? ((this.personalisationData && this.personalisationData.color) || this.selectedColor || '') : '';
    const fontName = supported.font ? ((this.personalisationData && this.personalisationData.font) || this.selectedFont || '') : '';

    const colorMap = {
      black: '#000000',
      white: '#ffffff',
      blue: '#DEE8EF',
      gold: '#DEB035',
      green: '#E4EFDB',
      grey: '#E8EBEC',
      gray: '#E8EBEC',
      multicolour: '#8B4789',
      orange: '#F8CF89',
      pink: '#F7DDE2',
      purple: '#F0D9E6',
      red: '#BC3725',
      silver: '#DEEBF7',
      yellow: '#F9F3DB'
    };
    const colorKey = colorName && colorName.toLowerCase ? colorName.toLowerCase() : '';
    const cssColor = (colorKey && colorMap[colorKey]) || colorName || '';

    weightOverlay.textContent = weightValue;
    weightOverlay.style.fontSize = `${fontSize}px`;
    weightOverlay.style.fontFamily = fontName ? `"${fontName}", sans-serif` : '';
    weightOverlay.style.transform = 'translate(-50%, 50%)';
    weightOverlay.style.top = '';
    weightOverlay.style.color = (supported.color && cssColor) ? cssColor : 'inherit';
    weightOverlay.style.display = '';

    if (!wrap || !img || !img.naturalWidth) return;
    const wrapW = wrap.clientWidth;
    const wrapH = wrap.clientHeight;
    if (!wrapW || !wrapH) return;
    const nw = img.naturalWidth;
    const nh = img.naturalHeight;
    const scale = Math.min(wrapW / nw, wrapH / nh);
    const vW = nw * scale;
    const vH = nh * scale;
    const vL = (wrapW - vW) / 2;
    const vT = (wrapH - vH) / 2;
    const leftPx = vL + (vW * xPct / 100);
    const bottomPx = wrapH - vT - (vH * (1 - yPct / 100));
    weightOverlay.style.left = Math.round(leftPx) + 'px';
    weightOverlay.style.bottom = Math.round(bottomPx) + 'px';
  }

  /**
   * Handles name input changes
   * @param {Event} event - The input event
   */
  handleNameInput = (event) => {
    const input = event.target;
    const value = input.value;

    const length = value.length;
    this.updateCharCounter(length);
    this.personalisationData.name = value;
    
    // Update save button state
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  };

  /**
   * Handles name tab input changes (Baby/Kid/Mum)
   * @param {Event} event - The input event
   */
  handleNameTabInput = (event) => {
    const input = event.target;
    const tabType = input.dataset.nameInput; // 'baby', 'kid', or 'mum'

    const value = input.value.trim();
    const length = input.value.length;
    
    // Update character counter
    this.updateNameTabCharCounter(tabType, length);
    
    // Update personalisation data
    if (tabType === 'baby') {
      this.personalisationData.babyName = value;
    } else if (tabType === 'kid') {
      this.personalisationData.kidName = value;
    } else if (tabType === 'mum') {
      this.personalisationData.mumName = value;
    }
    
    // Show/hide tick icon based on whether value exists
    this.updateNameTabIcon(tabType, value.length > 0);
    
    // Update save button state
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  };
  
  /**
   * Updates the character counter for name tab inputs
   * @param {string} tabType - The tab type ('baby', 'kid', or 'mum')
   * @param {number} count - The current character count
   */
  updateNameTabCharCounter(tabType, count) {
    let charCounter = null;
    if (tabType === 'baby') {
      charCounter = this.refs.babyCharCounter?.querySelector('[ref="babyCharCount"]') || this.refs.babyCharCount;
    } else if (tabType === 'kid') {
      charCounter = this.refs.kidCharCounter?.querySelector('[ref="kidCharCount"]') || this.refs.kidCharCount;
    } else if (tabType === 'mum') {
      charCounter = this.refs.mumCharCounter?.querySelector('[ref="mumCharCount"]') || this.refs.mumCharCount;
    }
    
    if (charCounter) {
      charCounter.textContent = count;
    }
  }

  /**
   * Switches between name tabs (Baby/Kid/Mum)
   * @param {Event} event - The click event
   */
  switchNameTab = (event) => {
    const button = event.target.closest('.personalise-name-tabs__tab');
    if (!button) return;
    
    const tabType = button.dataset.tab; // 'baby', 'kid', or 'mum'
    
    // Get all tabs and panels
    const tabsContainer = this.querySelector('.personalise-name-tabs__tablist');
    const panelsContainer = this.querySelector('.personalise-name-tabs__panels');
    
    if (!tabsContainer || !panelsContainer) return;
    
    // Remove active class from all tabs
    const allTabs = tabsContainer.querySelectorAll('.personalise-name-tabs__tab');
    allTabs.forEach(tab => {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-selected', 'false');
    });
    
    // Add active class to clicked tab
    button.classList.add('is-active');
    button.setAttribute('aria-selected', 'true');
    
    // Hide all panels
    const allPanels = panelsContainer.querySelectorAll('.personalise-name-tabs__panel');
    allPanels.forEach(panel => {
      panel.classList.remove('is-active');
    });
    
    // Show corresponding panel
    const activePanel = panelsContainer.querySelector(`[data-panel="${tabType}"]`);
    if (activePanel) {
      activePanel.classList.add('is-active');
    }
    this.updateCbPreviewOverlay();
  };

  /**
   * Updates the tick icon visibility for a name tab
   * @param {string} tabType - The tab type ('baby', 'kid', or 'mum')
   * @param {boolean} show - Whether to show the icon
   */
  updateNameTabIcon(tabType, show) {
    const icon = this.querySelector(`[data-tab-icon="${tabType}"]`);
    if (icon) {
      icon.style.display = show ? 'inline-flex' : 'none';
    } else {
      console.warn(`Tab icon not found for type: ${tabType}`, this.querySelectorAll('[data-tab-icon]'));
    }
  };

  /**
   * Updates the character counter
   * @param {number} count - The current character count
   */
  updateCharCounter(count) {
    const charCounter = this.refs.charCounter?.querySelector('[ref="charCount"]') || this.refs.charCount;
    if (charCounter) {
      charCounter.textContent = count;
    }
  }

  /**
   * Handles color selection
   * @param {Event} event - The click event
   */
  selectColor = (event) => {
    const button = event.target.closest('.personalise-modal__color-button');
    if (!button) return;

    const colorName = button.dataset.color;
    this.selectColorByName(colorName);
  };

  /**
   * Selects a color by name
   * @param {string} colorName - The color name to select
   */
  selectColorByName(colorName) {
    const colorGrid = this.refs.colorGrid || this.querySelector('.personalise-modal__color-grid');
    if (!colorGrid || !colorName) return;

    // Uncheck all radio inputs and remove selected class
    const radioInputs = colorGrid.querySelectorAll('input[type="radio"]');
    radioInputs.forEach(input => {
      input.checked = false;
    });
    colorGrid.querySelectorAll('.personalise-modal__color-button').forEach((l) => l.classList.remove('personalise-modal__color-button--selected'));

    // Match case-insensitively (data-color is lowercase, saved value may be original case e.g. "Pink")
    // Also match by radio value as fallback (some contexts may use different structure)
    const colorNameLower = String(colorName).toLowerCase().trim();
    const selectedButton = Array.from(colorGrid.querySelectorAll('.personalise-modal__color-button')).find(
      btn => {
        const btnColor = (btn.dataset.color || '').toLowerCase();
        const radioValue = (btn.querySelector('input[type="radio"]')?.value || '').toLowerCase();
        return btnColor === colorNameLower || (radioValue && radioValue === colorNameLower);
      }
    );
    if (selectedButton) {
      const radioInput = selectedButton.querySelector('input[type="radio"]');
      if (radioInput) {
        radioInput.checked = true;
      }
      selectedButton.classList.add('personalise-modal__color-button--selected');
      // Use radio value for consistency (preserves original case for form submission)
      const storedColor = radioInput?.value || colorName;
      this.selectedColor = storedColor;
      this.personalisationData.color = storedColor;
    }
    
    // Update save button state after color selection
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  }

  /**
   * Handles font selection
   * @param {Event} event - The click event
   */
  selectFont = (event) => {
    const button = event.target.closest('.personalise-modal__font-button');
    if (!button) return;

    const fontName = button.dataset.font;
    this.selectFontByName(fontName);
  };

  /**
   * Selects a font by name
   * @param {string} fontName - The font name to select
   */
  selectFontByName(fontName) {
    if (!this.refs.fontGrid) return;

    // Remove selected class from all buttons
    const buttons = this.refs.fontGrid.querySelectorAll('.personalise-modal__font-button');
    buttons.forEach(btn => {
      btn.classList.remove('personalise-modal__font-button--selected');
    });

    // Add selected class to the clicked button
    const selectedButton = Array.from(buttons).find(
      btn => btn.dataset.font === fontName
    );
    if (selectedButton) {
      selectedButton.classList.add('personalise-modal__font-button--selected');
      this.selectedFont = fontName;
      this.personalisationData.font = fontName;
    }
    
    // Update save button state after font selection
    this.updateSaveButton();
    this.updateCbPreviewOverlay();
  }

  /**
   * Updates the save button enabled/disabled state
   */
  updateSaveButton() {
    if (this.refs.saveButton) {
      // Check if name field exists and has value (for personalized_name products)
      const nameInput = this.refs.nameInput || this.querySelector('#personalise-name');
      const hasName = nameInput ? nameInput.value.trim().length > 0 : true;
      
      // Check if baby/kid/mum name inputs exist and have values
      const babyNameInput = this.refs.babyNameInput || this.querySelector('[data-name-input="baby"]');
      const kidNameInput = this.refs.kidNameInput || this.querySelector('[data-name-input="kid"]');
      const mumNameInput = this.refs.mumNameInput || this.querySelector('[data-name-input="mum"]');
      
      const hasBabyName = babyNameInput ? babyNameInput.value.trim().length > 0 : true;
      const hasKidName = kidNameInput ? kidNameInput.value.trim().length > 0 : true;
      const hasMumName = mumNameInput ? mumNameInput.value.trim().length > 0 : true;
      
      // If any of these inputs exist, they must have values
      const babyNameRequired = babyNameInput && !hasBabyName;
      const kidNameRequired = kidNameInput && !hasKidName;
      const mumNameRequired = mumNameInput && !hasMumName;
      
      // Check if font field exists (required field with red asterisk)
      const fontGrid = this.refs.fontGrid || this.querySelector('.personalise-modal__font-grid');
      const hasFont = fontGrid ? (this.selectedFont || this.personalisationData.font) : true;
      const fontRequired = fontGrid && !hasFont;
      
      // Check if color field exists (required field with red asterisk)
      const colorGrid = this.refs.colorGrid || this.querySelector('.personalise-modal__color-grid');
      const colorRadioInputs = colorGrid ? colorGrid.querySelectorAll('input[type="radio"]') : [];
      const hasColor = colorGrid ? (this.selectedColor || this.personalisationData.color || Array.from(colorRadioInputs).some(input => input.checked)) : true;
      const colorRequired = colorGrid && colorRadioInputs.length > 0 && !hasColor;
      
      // Check for any other required fields (fields with required attribute or aria-required)
      const allInputs = this.querySelectorAll('input, textarea, select');
      let hasMissingRequiredField = false;
      allInputs.forEach(input => {
        // Skip radio buttons that aren't checked (they're handled separately)
        if (input.type === 'radio' && !input.checked) {
          return;
        }
        // Check if field is required
        if (input.hasAttribute('required') || input.hasAttribute('aria-required')) {
          // For text inputs, textareas, and selects, check if they have a value
          if ((input.type === 'text' || input.type === 'textarea' || input.tagName === 'TEXTAREA' || input.tagName === 'SELECT') && !input.value.trim()) {
            hasMissingRequiredField = true;
          }
          // For checkboxes, check if they're checked
          if (input.type === 'checkbox' && !input.checked) {
            hasMissingRequiredField = true;
          }
        }
      });
      
      // Disable save button if any required field is missing:
      // 1. Name input exists but is empty (for personalized_name products)
      // 2. Any baby/kid/mum name input exists but is empty
      // 3. Font field exists but no font is selected
      // 4. Color field exists but no color is selected
      // 5. Any other required field is missing
      if (nameInput && !hasName) {
        this.refs.saveButton.disabled = true;
      } else if (babyNameRequired || kidNameRequired || mumNameRequired) {
        this.refs.saveButton.disabled = true;
      } else if (fontRequired) {
        this.refs.saveButton.disabled = true;
      } else if (colorRequired) {
        this.refs.saveButton.disabled = true;
      } else if (hasMissingRequiredField) {
        this.refs.saveButton.disabled = true;
      } else {
        // All required fields have values, enable save button
        this.refs.saveButton.disabled = false;
      }
    }
  }

  /**
   * Saves the personalisation
   * @param {Event} event - The click event
   */
  savePersonalisation = async (event) => {
    event.preventDefault();
    
    // Collect all field values
    const nameInput = this.refs.nameInput || this.querySelector('#personalise-name');
    const name = nameInput ? nameInput.value.trim() : '';
    
    // Validate required fields (name is required for personalized_name products)
    if (nameInput && !name) {
      return;
    }

    // Collect Baby/Kid/Mum name values from inputs FIRST
    const babyNameInput = this.refs.babyNameInput || this.querySelector('[data-name-input="baby"]');
    const babyNameValue = babyNameInput && babyNameInput.value.trim() ? babyNameInput.value.trim() : null;
    
    const kidNameInput = this.refs.kidNameInput || this.querySelector('[data-name-input="kid"]');
    const kidNameValue = kidNameInput && kidNameInput.value.trim() ? kidNameInput.value.trim() : null;
    
    const mumNameInput = this.refs.mumNameInput || this.querySelector('[data-name-input="mum"]');
    const mumNameValue = mumNameInput && mumNameInput.value.trim() ? mumNameInput.value.trim() : null;

    // Collect all personalisation data - only include values for fields this product supports
    const supported = this.#getSupportedPersonalisationFields();
    const personalisation = {
      name: supported.name ? name : null,
      font: supported.font ? (this.personalisationData.font || this.selectedFont) : null,
      color: supported.color ? (this.personalisationData.color || this.selectedColor) : null,
      dob: null,
      schoolYear: null,
      name1: null,
      name2: null,
      name3: null,
      name4: null,
      textbox: null,
      message: null,
      optionalDob: null,
      time: null,
      weight: null,
      babyName: supported.babyName ? babyNameValue : null,
      kidName: supported.kidName ? kidNameValue : null,
      mumName: supported.mumName ? mumNameValue : null
    };

    // Collect optional fields (only from inputs that exist = product supports them)
    const dobInput = this.refs.dobInput || this.querySelector('input[name="properties[Date of Birth]"]');
    if (dobInput && dobInput.value.trim()) {
      personalisation.dob = this.#dateInputValueToDobProperty(dobInput.value.trim());
    }

    const schoolYearInput = this.refs.schoolYearInput || this.querySelector('input[name="properties[School Year]"]');
    if (schoolYearInput) personalisation.schoolYear = schoolYearInput.value.trim();

    const name1Input = this.refs.name1Input || this.querySelector('input[name="properties[Name 1]"]');
    if (name1Input) personalisation.name1 = name1Input.value.trim();

    const name2Input = this.refs.name2Input || this.querySelector('input[name="properties[Name 2]"]');
    if (name2Input) personalisation.name2 = name2Input.value.trim();

    const name3Input = this.refs.name3Input || this.querySelector('input[name="properties[Name 3]"]');
    if (name3Input) personalisation.name3 = name3Input.value.trim();

    const name4Input = this.refs.name4Input || this.querySelector('input[name="properties[Name 4]"]');
    if (name4Input) personalisation.name4 = name4Input.value.trim();

    const textboxInput = this.refs.textboxInput || this.querySelector('[name="properties[Personalisation:]"]');
    if (textboxInput) personalisation.textbox = textboxInput.value.trim();

    const messageInput = this.refs.messageInput || this.querySelector('textarea[name="properties[Message]"]');
    if (messageInput) personalisation.message = messageInput.value.trim();

    const optionalDobInput = this.refs.optionalDobInput || this.querySelector('#dob_field_val');
    if (optionalDobInput && optionalDobInput.value) {
      personalisation.optionalDob = this.#dateInputValueToDobProperty(optionalDobInput.value);
    }

    const timeInput = this.refs.timeInput || this.querySelector('input[name="properties[Time]"]');
    if (timeInput) personalisation.time = timeInput.value.trim();

    const weightInput = this.refs.weightInput || this.querySelector('input[name="properties[Weight]"]');
    if (weightInput) personalisation.weight = weightInput.value.trim();

    // Update personalisationData for next time
    if (babyNameValue !== null) this.personalisationData.babyName = babyNameValue;
    if (kidNameValue !== null) this.personalisationData.kidName = kidNameValue;
    if (mumNameValue !== null) this.personalisationData.mumName = mumNameValue;

    // Only keep fields this product supports (prevents Product A's font/color etc. being saved for Product B)
    const filteredPersonalisation = this.#filterToSupportedFields(personalisation);

    // Check if we're editing from cart context
    const cartContext = window.cartPersonalizationContext;
    
    // Declare form variable outside if/else so it's accessible later
    let form = null;
    
    if (cartContext && cartContext.cartLine) {
      // Update cart item properties ONLY - do NOT update product page form
      // The product page form should keep its original personalization
      await this.#updateCartItemProperties(cartContext, filteredPersonalisation);
      
      // Find form only for event detail, but don't modify it
      form = this.closest('product-form-component')?.querySelector('form[data-type="add-to-cart-form"]');
      if (!form) {
        const quickAddModal = document.querySelector('#quick-add-modal-content');
        if (quickAddModal) {
          form = quickAddModal.querySelector('form[data-type="add-to-cart-form"]');
        }
      }
      if (!form) {
        form = document.querySelector('form[data-type="add-to-cart-form"]');
      }
      
    } else {
      // Write personalisation directly to form inputs (not to storage)
      // CRITICAL: Use #getProductForm() to get the form for THIS product (by product ID)
      // This prevents adding wrong product when personalise-dialog is in body or multiple forms exist
      form = this.#getProductForm();

      if (form) {
        // Remove existing personalisation properties (but keep gift message and other non-personalisation properties)
        const existingProps = form.querySelectorAll('input[name^="properties["], textarea[name^="properties["]');
        existingProps.forEach(input => {
          const name = input.name;
          // Only remove personalisation-related properties, not gift message or other properties
          if (name.includes('[Name]') || 
              name.includes('[Text Font]') || 
              name.includes('[Text Colour]') || 
              name.includes('[Date of Birth]') || 
              name.includes('[School Year]') || 
              name.includes('[Name 1]') || 
              name.includes('[Name 2]') || 
              name.includes('[Name 3]') || 
              name.includes('[Name 4]') || 
              name.includes('[Personalisation:]') || 
              name.includes('[Personalise Date of Birth]') || 
              name.includes('[Time]') || 
              name.includes('[Weight]') ||
              name.includes('[Baby\'s Name]') ||
              name.includes('[Kid\'s Name]') ||
              name.includes('[Mum\'s Name]') ||
              name.includes('[Message]')) {
            input.remove();
          }
        });
        
        // Add personalisation properties as hidden inputs (only supported fields)
        const addProperty = (name, value) => {
          if (value && value.toString().trim() !== '') {
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value.toString().trim();
            // Insert before the variant ID input to ensure it's in the form
            const variantInput = form.querySelector('input[name="id"]');
            if (variantInput) {
              variantInput.parentNode.insertBefore(input, variantInput.nextSibling);
            } else {
              form.appendChild(input);
            }
          }
        };
        
        if (filteredPersonalisation.name) addProperty('properties[Name]', filteredPersonalisation.name);
        if (filteredPersonalisation.font) addProperty('properties[Text Font]', filteredPersonalisation.font);
        if (filteredPersonalisation.color) addProperty('properties[Text Colour]', filteredPersonalisation.color);
        if (filteredPersonalisation.dob) addProperty('properties[Date of Birth]', filteredPersonalisation.dob);
        if (filteredPersonalisation.schoolYear) addProperty('properties[School Year]', filteredPersonalisation.schoolYear);
        if (filteredPersonalisation.name1) addProperty('properties[Name 1]', filteredPersonalisation.name1);
        if (filteredPersonalisation.name2) addProperty('properties[Name 2]', filteredPersonalisation.name2);
        if (filteredPersonalisation.name3) addProperty('properties[Name 3]', filteredPersonalisation.name3);
        if (filteredPersonalisation.name4) addProperty('properties[Name 4]', filteredPersonalisation.name4);
        if (filteredPersonalisation.textbox) addProperty('properties[Personalisation:]', filteredPersonalisation.textbox);
        if (filteredPersonalisation.message) addProperty('properties[Message]', filteredPersonalisation.message);
        if (filteredPersonalisation.optionalDob) addProperty('properties[Personalise Date of Birth]', filteredPersonalisation.optionalDob);
        if (filteredPersonalisation.time) addProperty('properties[Time]', filteredPersonalisation.time);
        if (filteredPersonalisation.weight) addProperty('properties[Weight]', filteredPersonalisation.weight);
        if (filteredPersonalisation.babyName) addProperty('properties[Baby\'s Name]', filteredPersonalisation.babyName);
        if (filteredPersonalisation.kidName) addProperty('properties[Kid\'s Name]', filteredPersonalisation.kidName);
        if (filteredPersonalisation.mumName) addProperty('properties[Mum\'s Name]', filteredPersonalisation.mumName);
        
        // Verify inputs were added
        const addedInputs = form.querySelectorAll('input[name^="properties["]');
        const inputDetails = Array.from(addedInputs).map(input => ({
          name: input.name,
          value: input.value
        }));
        
        // Use requestAnimationFrame to ensure DOM is updated before checking
        requestAnimationFrame(() => {
          // Trigger update of button text - with multiple retries to ensure buttons are in DOM
          const updateButtonWithRetry = (attempts = 0) => {
            if (typeof window.updatePersonaliseButtonText === 'function') {
              // Verify inputs are still in the form before updating
              const verifyInputs = form.querySelectorAll('input[name^="properties["]');
              
              window.updatePersonaliseButtonText();
              
              // Check if buttons were updated, if not retry
              setTimeout(() => {
                const buttons = document.querySelectorAll('[data-personalise-button]');
                let anyButtonUpdated = false;
                buttons.forEach((button) => {
                  const textSpan = button.querySelector('[data-personalise-text]');
                  if (textSpan && textSpan.textContent === 'EDIT') {
                    anyButtonUpdated = true;
                  }
                });
                
                // If no buttons were updated and we haven't exceeded retries, try again
                if (!anyButtonUpdated && attempts < 15) {
                  setTimeout(() => updateButtonWithRetry(attempts + 1), 200);
                }
              }, 100);
            } else if (attempts < 15) {
              // Function not defined yet, retry
              setTimeout(() => updateButtonWithRetry(attempts + 1), 200);
            }
          };
          
          // Start with a small delay to ensure DOM is ready
          setTimeout(() => updateButtonWithRetry(0), 50);
        });
      } else {
      }

      // Store globally keyed by variant ID (for form submission precision) and product ID (for modal UX)
      const productId = form?.closest('product-form-component')?.dataset?.productId;
      const variantId = form?.querySelector('input[name="id"]')?.value;
      if (!window.currentPersonalisation) {
        window.currentPersonalisation = {};
      }
      if (variantId) {
        window.currentPersonalisation[variantId] = filteredPersonalisation;
      }
      if (productId) {
        window.currentPersonalisation[productId] = filteredPersonalisation;
      }

      // Update hidden form fields if they exist
      this.updateFormFields(filteredPersonalisation);

      // When saving with color after variant change: clear "Please select colour" state immediately
      // so add-to-cart enables when user checks confirmation (updatePersonaliseButtonText runs later)
      if (filteredPersonalisation.color && form) {
        const allForms = this.#getAllProductForms();
        allForms.forEach((f) => f.removeAttribute('data-personalisation-color-required'));
        const formComp = form.closest('product-form-component');
        const pid = formComp?.dataset?.productId || this.dataset?.productId;
        const searchRoots = [
          formComp,
          form.closest('#quick-add-modal-content'),
          pid ? document.querySelector(`sticky-add-to-cart[data-product-id="${pid}"]`) : null
        ].filter(Boolean);
        for (const root of searchRoots) {
          const msgEl = root.querySelector('[data-personalise-color-required-msg]');
          if (msgEl) msgEl.style.display = 'none';
          const confirmation = root.querySelector('[data-personalise-confirmation]');
          if (confirmation) confirmation.style.display = '';
        }
        if (typeof window.updateAddToCartButtonState === 'function') {
          window.updateAddToCartButtonState();
        }
      }
    }

    // Dispatch custom event for other components to listen to
    // Include form reference and product ID in event detail
    const formComponent = form?.closest('product-form-component');
    const productId = formComponent?.dataset?.productId || this.dataset?.productId;
    
    const customEvent = new CustomEvent('personalisation-saved', {
      detail: {
        ...filteredPersonalisation,
        form: form,
        formId: form?.id,
        productId: productId,
        formComponent: formComponent
      },
      bubbles: true,
      cancelable: true
    });
    this.dispatchEvent(customEvent);
    
    // Also dispatch on document to ensure it's caught
    document.dispatchEvent(customEvent);

    // When full_screen_personalisation: add to cart directly instead of just saving
    const isAddToCartMode = this.refs?.saveButton?.dataset?.addToCartMode === 'true';
    if (isAddToCartMode && form && !cartContext) {
      const saveButton = this.refs.saveButton;
      const addToCartText = saveButton?.dataset?.addToCartText || 'Add to cart';
      const addingText = saveButton?.dataset?.addingText || 'Adding...';

      // Show "Adding" state while submitting
      if (saveButton) {
        saveButton.disabled = true;
        saveButton.textContent = addingText;
      }

      // Ensure personalisation confirmation checkbox is checked (user confirmed by clicking Add to cart in modal)
      const formComponent = form.closest('product-form-component');
      const confirmCheckbox = formComponent?.querySelector('[data-personalise-confirm-checkbox]');
      if (confirmCheckbox) {
        confirmCheckbox.checked = true;
      }

      const refreshProductSection = () => {
        const productId = this.dataset?.productId;
        const productPagePicker = productId
          ? document.querySelector(`variant-picker[data-product-id="${productId}"]`)
          : document.querySelector('variant-picker');
        if (productPagePicker && !productPagePicker.closest('#quick-add-modal-content')) {
          const sectionEl = productPagePicker.closest('.shopify-section');
          if (sectionEl?.id) {
            const sectionId = normalizeSectionId(sectionEl.id);
            const url = new URL(window.location.href);
            sectionRenderer.renderSection(sectionId, { cache: false, url });
          }
        }
      };

      // Submit the form - wait for CartAddEvent (success) before closing so cart drawer opens and refreshes first
      const handleCartAdd = (event) => {
        const didError = event?.detail?.data?.didError;
        document.removeEventListener(ThemeEvents.cartUpdate, handleCartAdd);
        if (this._addToCartCloseTimeout) {
          clearTimeout(this._addToCartCloseTimeout);
          this._addToCartCloseTimeout = null;
        }
        if (!didError) {
          this.closeDialog();
          // Refresh product section to reset personalisations (fetch whole section)
          requestAnimationFrame(() => setTimeout(refreshProductSection, 100));
        } else {
          // Restore button on error
          if (saveButton) {
            saveButton.disabled = false;
            saveButton.textContent = addToCartText;
          }
        }
      };
      document.addEventListener(ThemeEvents.cartUpdate, handleCartAdd);
      // Fallback: close after 5s if event never fires (e.g. network error)
      this._addToCartCloseTimeout = setTimeout(() => {
        document.removeEventListener(ThemeEvents.cartUpdate, handleCartAdd);
        this.closeDialog();
        refreshProductSection();
      }, 5000);
      form.requestSubmit();
    } else {
      // Normal save flow - close immediately
      this.closeDialog();
    }

    // Button text is updated by document listener (buy-buttons) on 'personalisation-saved'.
    // We do not update here to avoid duplicate retry loops that caused the Edit
    // personalisation button to flash in the quick-add popup.
    
    // Clear cart context after save
    if (cartContext) {
      window.cartPersonalizationContext = null;
    }
  };

  /**
   * Updates cart item properties using cart/change.js
   * @param {Object} cartContext - The cart context with cartLine, productId, variantId
   * @param {Object} personalisation - The personalisation data
   * @private
   */
  async #updateCartItemProperties(cartContext, personalisation) {
    try {
      // First, fetch current cart to get the existing quantity for this line item
      const cartResponse = await fetch('/cart.js');
      const currentCart = await cartResponse.json();
      
      // Find the line item by line index (1-based)
      const lineIndex = parseInt(cartContext.cartLine) - 1; // Convert to 0-based index
      const currentCartItem = currentCart.items[lineIndex];
      
      if (!currentCartItem) {
        throw new Error('Cart item not found');
      }
      
      // Get the current quantity to preserve it
      const currentQuantity = currentCartItem.quantity || 1;
      
      // Start with existing properties so we preserve add-on links and other metadata.
      const properties = { ...(currentCartItem.properties || {}) };

      const setOrDelete = (key, value) => {
        if (value && String(value).trim() !== '') {
          properties[key] = String(value).trim();
        } else if (key in properties) {
          delete properties[key];
        }
      };

      setOrDelete('Name', personalisation.name);
      setOrDelete('Name 1', personalisation.name1);
      setOrDelete('Name 2', personalisation.name2);
      setOrDelete('Name 3', personalisation.name3);
      setOrDelete('Name 4', personalisation.name4);
      setOrDelete("Baby's Name", personalisation.babyName);
      setOrDelete("Kid's Name", personalisation.kidName);
      setOrDelete("Mum's Name", personalisation.mumName);
      setOrDelete('Date of Birth', personalisation.dob);
      setOrDelete('Personalise Date of Birth', personalisation.optionalDob);
      setOrDelete('School Year', personalisation.schoolYear);
      setOrDelete('Text Colour', personalisation.color);
      setOrDelete('Text Font', personalisation.font);
      setOrDelete('Personalisation:', personalisation.textbox);
      setOrDelete('Message', personalisation.message);
      setOrDelete('Time', personalisation.time);
      setOrDelete('Weight', personalisation.weight);
      
      // Get cart sections to update - find all cart-items-components
      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionIdSet = new Set();
      cartItemsComponents.forEach(comp => {
        if (comp.dataset?.sectionId) {
          sectionIdSet.add(comp.dataset.sectionId);
        }
      });
      const sectionIds = Array.from(sectionIdSet).join(',');
      
      // Build request body - include quantity to preserve it
      const body = {
        line: cartContext.cartLine,
        quantity: currentQuantity,
        properties: properties
      };
      
      // Add sections parameter if we have section IDs (comma-separated string)
      if (sectionIds) {
        body.sections = sectionIds;
        // Also include sections_url for proper section rendering
        body.sections_url = window.location.pathname;
      }
      
      // Update cart via AJAX
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });
      
      if (!response.ok) {
        throw new Error(`Cart update failed: ${response.statusText}`);
      }
      
      const cart = await response.json();
      
      // If cart API returned sections, use them to update the DOM
      if (cart.sections) {
        // Update each section
        Object.keys(cart.sections).forEach(sectionId => {
          if (cart.sections[sectionId]) {
            morphSection(sectionId, cart.sections[sectionId]);
          }
        });
      } else if (sectionIds) {
        // Fetch updated cart sections manually
        const sectionsResponse = await fetch(`/?sections=${sectionIds}`);
        const sectionsData = await sectionsResponse.json();
        
        Object.keys(sectionsData).forEach(sectionId => {
          if (sectionsData[sectionId]) {
            morphSection(sectionId, sectionsData[sectionId]);
          }
        });
      } else {
        // Fallback: reload the page
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Error updating cart item properties:', error);
    }
  }

  /**
   * Updates form fields with personalisation data.
   * Only adds properties for fields this product supports.
   * @param {Object} personalisation - The personalisation data
   */
  updateFormFields(personalisation) {
    const filtered = this.#filterToSupportedFields(personalisation || {});
    if (Object.keys(filtered).length === 0) return;

    // Find the product form - try multiple methods to ensure we get the right one
    let productForm = null;
    
    // First, try to find form associated with this dialog's product
    const productFormComponent = this.closest('product-form-component');
    if (productFormComponent) {
      productForm = productFormComponent.querySelector('form[data-type="add-to-cart-form"]');
    }
    
    // If not found, check if we're in a quick-add modal context
    if (!productForm) {
      const quickAddModal = document.querySelector('#quick-add-modal-content');
      if (quickAddModal) {
        // Find the form within the quick-add modal
        productForm = quickAddModal.querySelector('form[data-type="add-to-cart-form"]');
      }
    }
    
    // Fallback: find any product form
    if (!productForm) {
      productForm = document.querySelector(`form[data-type="add-to-cart-form"]`);
    }
    
    if (!productForm) {
      return;
    }

    // Remove existing personalisation properties fields (but keep gift message and other non-personalisation properties)
    const existingProps = productForm.querySelectorAll('input[name^="properties["], textarea[name^="properties["]');
    existingProps.forEach(input => {
      const name = input.name;
      // Only remove personalisation-related properties, not gift message or other properties
      if (name.includes('[Name]') || 
          name.includes('[Text Font]') || 
          name.includes('[Text Colour]') || 
          name.includes('[Date of Birth]') || 
          name.includes('[School Year]') || 
          name.includes('[Name 1]') || 
          name.includes('[Name 2]') || 
          name.includes('[Name 3]') || 
          name.includes('[Name 4]') || 
          name.includes('[Personalisation:]') || 
          name.includes('[Personalise Date of Birth]') || 
          name.includes('[Time]') || 
          name.includes('[Weight]') ||
          name.includes('[Baby\'s Name]') ||
          name.includes('[Kid\'s Name]') ||
          name.includes('[Mum\'s Name]') ||
          name.includes('[Message]')) {
        input.remove();
      }
    });

    // Helper function to add hidden input
    const addProperty = (name, value) => {
      if (value && value.toString().trim() !== '') {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value.toString().trim();
        productForm.appendChild(input);
      }
    };

    // Add all personalisation properties (filtered to supported fields only)
    if (filtered.name) {
      addProperty('properties[Name]', filtered.name);
    }

    if (filtered.font) {
      addProperty('properties[Text Font]', filtered.font);
    }

    if (filtered.color) {
      addProperty('properties[Text Colour]', filtered.color);
    }

    if (filtered.dob) {
      addProperty('properties[Date of Birth]', filtered.dob);
    }

    if (filtered.schoolYear) {
      addProperty('properties[School Year]', filtered.schoolYear);
    }

    if (filtered.name1) {
      addProperty('properties[Name 1]', filtered.name1);
    }

    if (filtered.name2) {
      addProperty('properties[Name 2]', filtered.name2);
    }

    if (filtered.name3) {
      addProperty('properties[Name 3]', filtered.name3);
    }

    if (filtered.name4) {
      addProperty('properties[Name 4]', filtered.name4);
    }

    if (filtered.textbox) {
      addProperty('properties[Personalisation:]', filtered.textbox);
    }

    if (filtered.message) {
      addProperty('properties[Message]', filtered.message);
    }

    if (filtered.optionalDob) {
      addProperty('properties[Personalise Date of Birth]', filtered.optionalDob);
    }

    if (filtered.time) {
      addProperty('properties[Time]', filtered.time);
    }

    if (filtered.weight) {
      addProperty('properties[Weight]', filtered.weight);
    }

    if (filtered.babyName) {
      addProperty('properties[Baby\'s Name]', filtered.babyName);
    }

    if (filtered.kidName) {
      addProperty('properties[Kid\'s Name]', filtered.kidName);
    }

    if (filtered.mumName) {
      addProperty('properties[Mum\'s Name]', filtered.mumName);
    }
    
    // Also set up a listener to re-add fields before form submission
    this.setupFormSubmitListener(productForm);
  }
  
  /**
   * Sets up a listener to ensure personalisation fields are added before form submission
   * @param {HTMLFormElement} form - The form element
   */
  setupFormSubmitListener(form) {
    // Remove any existing listener to avoid duplicates
    if (form.dataset.personalisationListenerAdded) {
      return;
    }
    
    form.dataset.personalisationListenerAdded = 'true';
    
    // Helper function to add personalisation fields to form
    const addPersonalisationFieldsToForm = (targetForm) => {
      // Get the current product ID
      let productFormComponent = targetForm.closest('product-form-component');
      let productId = productFormComponent?.dataset?.productId;
      
      // If not found, check if we're in a quick-add modal context
      if (!productId) {
        const quickAddModal = document.querySelector('#quick-add-modal-content');
        if (quickAddModal) {
          productFormComponent = quickAddModal.querySelector('product-form-component');
          productId = productFormComponent?.dataset?.productId;
        }
      }
      
      if (!productId) {
        return;
      }
      
      // Read personalisation directly from form inputs (not from storage)
      const personalisation = {};
      
      // Read all personalisation fields from form
      const nameInput = targetForm.querySelector('input[name="properties[Name]"]');
      if (nameInput && nameInput.value.trim()) {
        personalisation.name = nameInput.value.trim();
      }
      
      const fontInput = targetForm.querySelector('input[name="properties[Text Font]"]:checked') || 
                        targetForm.querySelector('input[name="properties[Text Font]"]');
      if (fontInput && fontInput.value.trim()) {
        personalisation.font = fontInput.value.trim();
      }
      
      const colorInput = targetForm.querySelector('input[name="properties[Text Colour]"]:checked') || 
                         targetForm.querySelector('input[name="properties[Text Colour]"]');
      if (colorInput && colorInput.value.trim()) {
        personalisation.color = colorInput.value.trim();
      }
      
      const dobInput = targetForm.querySelector('input[name="properties[Date of Birth]"]');
      if (dobInput && dobInput.value.trim()) {
        personalisation.dob = this.#dateInputValueToDobProperty(dobInput.value.trim());
      }
      
      const schoolYearInput = targetForm.querySelector('input[name="properties[School Year]"]');
      if (schoolYearInput && schoolYearInput.value.trim()) {
        personalisation.schoolYear = schoolYearInput.value.trim();
      }
      
      const name1Input = targetForm.querySelector('input[name="properties[Name 1]"]');
      if (name1Input && name1Input.value.trim()) {
        personalisation.name1 = name1Input.value.trim();
      }
      
      const name2Input = targetForm.querySelector('input[name="properties[Name 2]"]');
      if (name2Input && name2Input.value.trim()) {
        personalisation.name2 = name2Input.value.trim();
      }
      
      const name3Input = targetForm.querySelector('input[name="properties[Name 3]"]');
      if (name3Input && name3Input.value.trim()) {
        personalisation.name3 = name3Input.value.trim();
      }
      
      const name4Input = targetForm.querySelector('input[name="properties[Name 4]"]');
      if (name4Input && name4Input.value.trim()) {
        personalisation.name4 = name4Input.value.trim();
      }
      
      const textboxInput = targetForm.querySelector('[name="properties[Personalisation:]"]');
      if (textboxInput && textboxInput.value.trim()) {
        personalisation.textbox = textboxInput.value.trim();
      }
      
      const messageInput = targetForm.querySelector('textarea[name="properties[Message]"]');
      if (messageInput && messageInput.value.trim()) {
        personalisation.message = messageInput.value.trim();
      }
      
      let optionalDobInput = targetForm.querySelector('input[name="properties[Personalise Date of Birth]"]');
      if (!optionalDobInput) {
        optionalDobInput = targetForm.querySelector('#dob_field_val');
      }
      if (optionalDobInput && optionalDobInput.value.trim()) {
        const raw = optionalDobInput.value.trim();
        personalisation.optionalDob = optionalDobInput.type === 'date' || /^\d{4}-\d{2}-\d{2}$/.test(raw)
          ? this.#dateInputValueToDobProperty(raw)
          : this.#normalizeDobPropertyString(raw);
      }
      
      const timeInput = targetForm.querySelector('input[name="properties[Time]"]');
      if (timeInput && timeInput.value.trim()) {
        personalisation.time = timeInput.value.trim();
      }
      
      const weightInput = targetForm.querySelector('input[name="properties[Weight]"]');
      if (weightInput && weightInput.value.trim()) {
        personalisation.weight = weightInput.value.trim();
      }
      
      const babyNameInput = targetForm.querySelector('input[name="properties[Baby\'s Name]"]');
      if (babyNameInput && babyNameInput.value.trim()) {
        personalisation.babyName = babyNameInput.value.trim();
      }
      
      const kidNameInput = targetForm.querySelector('input[name="properties[Kid\'s Name]"]');
      if (kidNameInput && kidNameInput.value.trim()) {
        personalisation.kidName = kidNameInput.value.trim();
      }
      
      const mumNameInput = targetForm.querySelector('input[name="properties[Mum\'s Name]"]');
      if (mumNameInput && mumNameInput.value.trim()) {
        personalisation.mumName = mumNameInput.value.trim();
      }
      
      // Check if we have any personalisation data from form inputs
      let hasPersonalisation = Object.keys(personalisation).length > 0;
      
      // ALWAYS check window.currentPersonalisation as the source of truth
      // This ensures we have the data even if inputs were removed
      let savedPersonalisation = null;
      
      // Try to get personalisation by product ID first
      if (window.currentPersonalisation && productId && window.currentPersonalisation[productId]) {
        savedPersonalisation = { ...window.currentPersonalisation[productId] };
      } else if (window.currentPersonalisation?._latest) {
        // Fallback to latest
        savedPersonalisation = { ...window.currentPersonalisation._latest };
      } else if (window.currentPersonalisation && typeof window.currentPersonalisation === 'object' && !Array.isArray(window.currentPersonalisation)) {
        // Check if it's a direct object (old format)
        const hasProductKeys = Object.keys(window.currentPersonalisation).some(key => key.startsWith('_') || /^\d+$/.test(key));
        if (!hasProductKeys) {
          savedPersonalisation = { ...window.currentPersonalisation };
        }
      }
      
      if (savedPersonalisation) {
        // Filter out null/empty values and internal keys
        Object.keys(savedPersonalisation).forEach(key => {
          if (key.startsWith('_') || 
              !savedPersonalisation[key] || 
              (typeof savedPersonalisation[key] === 'string' && !savedPersonalisation[key].trim())) {
            delete savedPersonalisation[key];
          }
        });
        // Merge with form inputs (form inputs take precedence if they exist)
        Object.assign(personalisation, savedPersonalisation);
        hasPersonalisation = Object.keys(personalisation).length > 0;
      } else {
      }
      
      if (!hasPersonalisation) {
        return;
      }
      
      try {
        
        // Remove existing personalisation properties fields (but keep gift message and other non-personalisation properties)
        const existingProps = targetForm.querySelectorAll('input[name^="properties["], textarea[name^="properties["]');
        let removedCount = 0;
        existingProps.forEach(input => {
          const name = input.name;
          // Only remove personalisation-related properties, not gift message or other properties
          // Check for exact matches or specific patterns - be very specific to avoid removing Gift Message
          const isPersonalisation = 
            name === 'properties[Name]' ||
            name === 'properties[Name 1]' ||
            name === 'properties[Name 2]' ||
            name === 'properties[Name 3]' ||
            name === 'properties[Name 4]' ||
            name.includes('[Text Font]') || 
            name.includes('[Text Colour]') || 
            (name.includes('[Date of Birth]') && !name.includes('Gift')) || 
            name.includes('[School Year]') || 
            name.includes('[Personalisation:]') || 
            name.includes('[Personalise Date of Birth]') || 
            name.includes('[Time]') || 
            name.includes('[Weight]') ||
            name.includes('[Baby\'s Name]') ||
            name.includes('[Kid\'s Name]') ||
            name.includes('[Mum\'s Name]') ||
            (name === 'properties[Message]' && !name.includes('Gift'));
          
          if (isPersonalisation) {
            input.remove();
            removedCount++;
            console.log('Removed personalisation input:', name);
          }
        });
        
        // Re-add all personalisation properties directly to the form
        const addProperty = (name, value) => {
          if (value && value.toString().trim() !== '') {
            // Remove any existing input with this exact name first
            const existing = targetForm.querySelector(`input[name="${name}"], textarea[name="${name}"]`);
            if (existing) {
              existing.remove();
            }
            
            const input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            input.value = value.toString().trim();
            // Insert before the variant ID input to ensure it's in the form
            const variantInput = targetForm.querySelector('input[name="id"]');
            if (variantInput) {
              variantInput.parentNode.insertBefore(input, variantInput.nextSibling);
            } else {
              targetForm.appendChild(input);
            }
            console.log('Added personalisation property to form:', name, '=', input.value);
          }
        };
        
        if (personalisation.name) addProperty('properties[Name]', personalisation.name);
        if (personalisation.font) addProperty('properties[Text Font]', personalisation.font);
        if (personalisation.color) addProperty('properties[Text Colour]', personalisation.color);
        if (personalisation.dob) addProperty('properties[Date of Birth]', personalisation.dob);
        if (personalisation.schoolYear) addProperty('properties[School Year]', personalisation.schoolYear);
        if (personalisation.name1) addProperty('properties[Name 1]', personalisation.name1);
        if (personalisation.name2) addProperty('properties[Name 2]', personalisation.name2);
        if (personalisation.name3) addProperty('properties[Name 3]', personalisation.name3);
        if (personalisation.name4) addProperty('properties[Name 4]', personalisation.name4);
        if (personalisation.textbox) addProperty('properties[Personalisation:]', personalisation.textbox);
        if (personalisation.message) addProperty('properties[Message]', personalisation.message);
        if (personalisation.optionalDob) addProperty('properties[Personalise Date of Birth]', personalisation.optionalDob);
        if (personalisation.time) addProperty('properties[Time]', personalisation.time);
        if (personalisation.weight) addProperty('properties[Weight]', personalisation.weight);
        if (personalisation.babyName) {
          addProperty('properties[Baby\'s Name]', personalisation.babyName);
          console.log('Added Baby\'s Name to form:', personalisation.babyName);
        }
        if (personalisation.kidName) {
          addProperty('properties[Kid\'s Name]', personalisation.kidName);
          console.log('Added Kid\'s Name to form:', personalisation.kidName);
        }
        if (personalisation.mumName) {
          addProperty('properties[Mum\'s Name]', personalisation.mumName);
          console.log('Added Mum\'s Name to form:', personalisation.mumName);
        }
        
        // Verify inputs are in the form
        const verifyInputs = targetForm.querySelectorAll('input[name^="properties["]');
        const personalisationInputs = Array.from(verifyInputs).filter(input => {
          const name = input.name;
          return name === 'properties[Name]' ||
                 name.includes('[Text Font]') || 
                 name.includes('[Text Colour]') || 
                 name.includes('[Date of Birth]') || 
                 name.includes('[School Year]') || 
                 name.includes('[Name 1]') || 
                 name.includes('[Name 2]') || 
                 name.includes('[Name 3]') || 
                 name.includes('[Name 4]') || 
                 name.includes('[Personalisation:]') || 
                 name.includes('[Personalise Date of Birth]') || 
                 name.includes('[Time]') || 
                 name.includes('[Weight]') ||
                 name.includes('[Baby\'s Name]') ||
                 name.includes('[Kid\'s Name]') ||
                 name.includes('[Mum\'s Name]') ||
                 name === 'properties[Message]';
        });
        
        console.log('Successfully added all personalisation fields to form before submit');
        console.log('Verification: Found', personalisationInputs.length, 'personalisation inputs in form after adding');
        personalisationInputs.forEach(input => {
          console.log('  - Personalisation input:', input.name, '=', input.value);
        });
        
        if (personalisationInputs.length === 0) {
          console.error('ERROR: No personalisation inputs found in form after adding!');
        }
      } catch (e) {
        console.error('Error re-adding personalisation fields:', e);
      }
    };
    
    // Listen for form submit event (capture phase to run early, before preventDefault)
    form.addEventListener('submit', (event) => {
      console.log('Form submit event detected, adding personalisation fields');
      addPersonalisationFieldsToForm(form);
    }, { capture: true, passive: false });
    
    // Also intercept the product-form-component's handleSubmit by listening on the component
    const productFormComponent = form.closest('product-form-component');
    if (productFormComponent) {
      // Store reference to the form for later use
      productFormComponent._personalisationForm = form;
      productFormComponent._addPersonalisationFields = addPersonalisationFieldsToForm;
      
      // Override or wrap the handleSubmit method to ensure fields are added BEFORE FormData is created
      const originalHandleSubmit = productFormComponent.handleSubmit;
      if (originalHandleSubmit && !productFormComponent._personalisationHandleSubmitWrapped) {
        productFormComponent._personalisationHandleSubmitWrapped = true;
        productFormComponent.handleSubmit = function(event) {
          console.log('ProductFormComponent handleSubmit called, adding personalisation fields');
          // Add fields BEFORE calling original handleSubmit (which creates FormData)
          if (this._addPersonalisationFields && this._personalisationForm) {
            this._addPersonalisationFields(this._personalisationForm);
            
            // Double-check that inputs are in the form before creating FormData
            const checkInputs = this._personalisationForm.querySelectorAll('input[name^="properties["]');
            console.log('Before FormData creation: Found', checkInputs.length, 'property inputs');
          }
          return originalHandleSubmit.call(this, event);
        };
      }
    }
    
    // Also listen for clicks on add-to-cart buttons as an additional safety measure
    const addToCartButtons = form.querySelectorAll('button[type="submit"], add-to-cart-component button, .add-to-cart-button');
    addToCartButtons.forEach(button => {
      button.addEventListener('click', () => {
        addPersonalisationFieldsToForm(form);
      }, { capture: true });
    });
  }
}

customElements.define('personalise-dialog', PersonaliseDialogComponent);
